import React, {
  FC,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import '../souhait.css';
import '../ui/ui.css';
import './PublicListPage.css';
import './ApplicationPolish.css';
import {Select} from '../ui/FormControls';
import {Icon, IconName} from '../ui/Icon';
import {Motif} from '../ui/Primitives';
import {TurnstileWidget} from '../ui/TurnstileWidget';
import {
  LegalPage,
  OwnerLoginPage,
  PricingPage,
  BillingReturnNotice,
  formatAccessDate,
} from './CommercialPages';
import {
  archiveOwnerWish,
  becomeCaptain,
  connectWishlistEvents,
  createOwnerWish,
  createOwnerWishlist,
  createSession,
  getOwnerSession,
  getSession,
  listOwnerWishlists,
  loadActivity,
  loadWishlist,
  logout,
  logoutOwner,
  openBillingPortal,
  paymentProviderCategory,
  pledgeToWish,
  previewOwnerMetadata,
  recoverSession,
  revealGiftMessages,
  reserveWish,
  sendCaptainUpdate,
  startBillingCheckout,
  trackEvent,
  updateContribution,
  updateOwnerWish,
  updateOwnerWishlistPayment,
  updateReservation,
} from '../souhait-api';
import {
  ActivityItem,
  ApiContributor,
  ApiOwnerAccount,
  ApiOwnerWishlist,
  ApiWishlist,
  SouhaitApiError,
} from '../souhait-api';

type WishMode = 'pledge' | 'gift' | 'choose';
type CaptureStep = 'url' | 'loading' | 'review';

interface Wish {
  id: string;
  number: string;
  title: string;
  note: string;
  image: string;
  merchant: string;
  price: number;
  pledged: number;
  pledgers: number;
  mode: WishMode;
  shape: string;
  sourceUrl: string;
  edition?: string;
  status?: string;
  custom?: boolean;
  myContributionId?: string | null;
  myReservationId?: string | null;
  isReserved?: boolean;
  hasCaptain?: boolean;
  deadline?: string;
  messageRevealed?: boolean;
  giftMessages?: string[];
  giftMessageCount?: number;
}

interface Pledge {
  id: string;
  wishId: string;
  amount: number;
  name: string;
  email: string;
  turnstileToken: string;
  message: string;
  recipientMessage: string;
  coordinationOptIn: boolean;
  createdAt: string;
}

interface MetadataDraft {
  url: string;
  title: string;
  merchant: string;
  image: string;
  price: string;
  edition: string;
  note: string;
  mode: WishMode;
  deadline: string;
  previewStatus?: 'complete' | 'partial';
  provider?: string;
}

interface CaptureModeOption {
  mode: WishMode;
  label: string;
  description: string;
}

const WISHES_KEY = 'souhait:wishes:v2';
const PLEDGES_KEY = 'souhait:pledges:v1';
const CAPTAINS_KEY = 'souhait:captains:v1';
const DEMO_MODE =
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_DEMO_MODE === 'true';
const WISHLIST_SLUG = /^[a-z0-9](?:[a-z0-9-]{0,46}[a-z0-9])?$/;

const initialWishes: Wish[] = [
  {
    id: 'game-ui-bible',
    number: '01',
    title: 'The Game UI Bible',
    note: 'The signed edition, if it is still available. A book I know I will keep returning to.',
    image: '/images/souhait/design-book.jpg',
    merchant: 'Lost in Cult',
    price: 120,
    pledged: 78,
    pledgers: 4,
    mode: 'pledge',
    shape: 'wish--wide',
    sourceUrl: 'https://www.lostincult.co.uk/gameuibible',
    edition: 'Signed limited edition',
    status: 'Pre-order',
  },
  {
    id: 'cobalt-chair',
    number: '02',
    title: 'A chair worth reading in',
    note: 'Deep blue, generous, and just strange enough. This is the one I keep thinking about.',
    image: '/images/souhait/cobalt-chair.jpg',
    merchant: 'Hem',
    price: 890,
    pledged: 0,
    pledgers: 0,
    mode: 'gift',
    shape: 'wish--portrait',
    sourceUrl: 'https://example.com/cobalt-chair',
  },
  {
    id: 'cabin-weekend',
    number: '03',
    title: 'Two quiet nights away',
    note: 'A small cabin, cold water, long walks, no notifications. Dates are completely flexible.',
    image: '/images/souhait/cabin-escape.jpg',
    merchant: 'Somewhere in the woods',
    price: 340,
    pledged: 215,
    pledgers: 7,
    mode: 'choose',
    shape: 'wish--tall',
    sourceUrl: 'https://example.com/cabin-weekend',
    edition: 'Dates flexible',
    status: 'Dream wish',
  },
];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value);

const formatDeadline = (value?: string) => {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const directionSixImage = (image: string) => {
  const replacements: Record<string, string> = {
    '/images/souhait/design-book.jpg':
      '/images/souhait/illustrations/wish-game-ui-book.webp',
    '/images/souhait/cobalt-chair.jpg':
      '/images/souhait/illustrations/wish-reading-chair.webp',
    '/images/souhait/cabin-escape.jpg':
      '/images/souhait/illustrations/wish-cabin-getaway.webp',
    '/images/souhait/hero-collection.jpg':
      '/images/souhait/illustrations/wish-fallback.webp',
  };
  return replacements[image] || image;
};

const useBodyLock = () => {
  useEffect(() => {
    document.body.classList.add('souhait-lock');
    return () => document.body.classList.remove('souhait-lock');
  }, []);
};

function safeRead<T>(key: string, fallback: T): T {
  try {
    const value = window.localStorage.getItem(key);
    return value ? (JSON.parse(value) as T) : fallback;
  } catch (error) {
    return fallback;
  }
}

const toTitle = (value: string) =>
  value
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, character => character.toUpperCase());

const fallbackMetadata = (url: string): MetadataDraft => {
  const parsed = new URL(url);
  const pathPart = parsed.pathname.split('/').filter(Boolean).pop() || 'A new wish';
  return {
    url,
    title: toTitle(pathPart),
    merchant: parsed.hostname.replace(/^www\./, ''),
    image: '/images/souhait/hero-collection.jpg',
    price: '',
    edition: '',
    note: '',
    mode: 'pledge',
    deadline: '',
  };
};

const extractMetadata = async (url: string): Promise<MetadataDraft> => {
  const fallback = fallbackMetadata(url);
  try {
    const {metadata} = await previewOwnerMetadata(url);
    const resolvedFallback = fallbackMetadata(metadata.url);
    const isAmazonFallback =
      metadata.provider === 'amazon' && !metadata.title;
    return {
      ...resolvedFallback,
      url: metadata.url,
      title:
        metadata.title ||
        (isAmazonFallback ? 'Produit Amazon' : resolvedFallback.title),
      merchant: metadata.merchant || resolvedFallback.merchant,
      image: metadata.imageUrl || resolvedFallback.image,
      price:
        !metadata.currency || metadata.currency === 'EUR'
          ? metadata.price.replace(',', '.')
          : '',
      previewStatus: metadata.status,
      provider: metadata.provider,
    };
  } catch (error) {
    return fallback;
  }
};

const safeExternalUrl = (value: string) => {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' ? url.toString() : '';
  } catch {
    return '';
  }
};

const trustedBillingUrl = (value: string) => {
  const url = safeExternalUrl(value);
  if (!url) return '';
  const parsed = new URL(url);
  const officialHost =
    parsed.hostname === 'lemonsqueezy.com' ||
    parsed.hostname.endsWith('.lemonsqueezy.com');
  const billingPath =
    parsed.pathname.startsWith('/checkout/') ||
    parsed.pathname === '/billing' ||
    parsed.pathname.startsWith('/billing/');
  return officialHost && billingPath ? url : '';
};

const redirectToBilling = (value: string) => {
  const url = trustedBillingUrl(value);
  if (!url) {
    throw new Error(
      'Lemon Squeezy a renvoyé une adresse de facturation invalide.'
    );
  }
  window.location.assign(url);
};

const captureModes: CaptureModeOption[] = [
  {
    mode: 'pledge',
    label: 'Participer ensemble',
    description: 'Chacun promet un montant. Aucun paiement ne passe par souha.it.',
  },
  {
    mode: 'gift',
    label: 'Réserver le cadeau',
    description: 'Une personne s’en charge sans révéler son nom.',
  },
  {
    mode: 'choose',
    label: 'Laisser le choix',
    description: 'La personne qui offre choisit la version finale.',
  },
];

const occasionPresets = [
  {
    value: 'birthday',
    label: 'Anniversaire',
    title: 'Mes souhaits d’anniversaire',
    subtitle: 'Quelques cadeaux qui me feraient vraiment plaisir cette année',
  },
  {
    value: 'departure',
    label: 'Départ',
    title: 'Un cadeau pour la suite',
    subtitle: 'Un cadeau choisi ensemble pour marquer ce nouveau départ',
  },
  {
    value: 'wedding',
    label: 'Mariage',
    title: 'Nos souhaits de mariage',
    subtitle: 'Quelques beaux objets pour notre vie ensemble',
  },
  {
    value: 'birth',
    label: 'Naissance',
    title: 'Bienvenue au bébé',
    subtitle: 'Des cadeaux utiles et durables, choisis avec soin',
  },
  {
    value: 'milestone',
    label: 'Grande occasion',
    title: 'Une occasion à célébrer',
    subtitle: 'Un cadeau qui compte, choisi ensemble',
  },
  {
    value: 'christmas',
    label: 'Noël',
    title: 'Mes envies pour Noël',
    subtitle: 'Des idées choisies avec soin, pour garder la surprise sans doublons',
  },
];

const modeLabel = (mode: WishMode) => {
  if (mode === 'gift') return 'À réserver';
  if (mode === 'choose') return 'Choix libre';
  return 'Participation commune';
};

const wishStatusLabel = (status: string) => {
  const labels: {[key: string]: string} = {
    reserved: 'Réservé', funded: 'Entièrement promis',
    purchased: 'Acheté', archived: 'Archivé',
  };
  return labels[status];
};

const occasionLabel = (occasion: string) =>
  occasionPresets.find(preset => preset.value === occasion)?.label ||
  toTitle(occasion);

const activityStatusLabel = (status: string) =>
  ({
    pledged: 'promesse enregistrée',
    settled: 'paiement confirmé',
    cancelled: 'annulé',
    pending: 'à confirmer',
    confirmed: 'achat confirmé',
    active: 'en cours',
    released: 'terminé',
  } as Record<string, string>)[status] || status;

const categoryForWish = (wish: Wish) => {
  const value = `${wish.title} ${wish.merchant} ${wish.note}`.toLowerCase();
  if (value.includes('book') || value.includes('bible') || value.includes('livre')) return 'Livres';
  if (value.includes('chair') || value.includes('furniture') || value.includes('chaise')) return 'Maison';
  if (
    value.includes('cabin') ||
    value.includes('weekend') ||
    value.includes('woods') ||
    value.includes('escapade') ||
    value.includes('séjour') ||
    value.includes('week-end') ||
    value.includes('voyage')
  )
    return 'Évasion';
  if (value.includes('stationery') || value.includes('notebook'))
    return 'Papeterie';
  if (value.includes('mug') || value.includes('kitchen')) return 'Cuisine';
  if (value.includes('tech') || value.includes('camera') || value.includes('appareil photo')) return 'Tech';
  if (value.includes('home') || value.includes('lamp')) return 'Maison';
  return 'Envies';
};

const catalogCategories: {label: string; icon: IconName; tone: string}[] = [
  {label: 'Tout', icon: 'grid', tone: 'mustard'},
  {label: 'Populaires', icon: 'heart', tone: 'terracotta'},
  {label: 'Nouveaux', icon: 'sparkle', tone: 'leaf'},
  {label: 'Maison', icon: 'home', tone: 'sky'},
  {label: 'Livres', icon: 'book-open', tone: 'terracotta'},
  {label: 'Évasion', icon: 'mountain', tone: 'leaf'},
  {label: 'Papeterie', icon: 'pen', tone: 'mustard'},
  {label: 'Envies', icon: 'gift', tone: 'terracotta'},
  {label: 'Tech', icon: 'camera', tone: 'sky'},
  {label: 'Cuisine', icon: 'cup', tone: 'mustard'},
];

const matchesCatalogCategory = (wish: Wish, category: string) =>
  category === 'Tout' ||
  category === 'Favoris' ||
  (category === 'Populaires' && wish.pledged > 0) ||
  (category === 'Nouveaux' && !wish.status && !wish.isReserved && wish.pledged === 0) ||
  category === categoryForWish(wish);

const RouteLink: FC<{
  href: string;
  navigate: (path: string) => void;
  className?: string;
  ariaLabel?: string;
}> = ({href, navigate, className, ariaLabel, children}) => (
  <a
    className={className}
    aria-label={ariaLabel}
    href={href}
    onClick={event => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      navigate(href);
    }}
  >
    {children}
  </a>
);

const Brand: FC<{navigate: (path: string) => void; light?: boolean}> = ({
  navigate,
  light = false,
}) => (
  <RouteLink
    className={`wordmark${light ? ' wordmark--light' : ''}`}
    href="/"
    navigate={navigate}
  >
    <img
      alt=""
      aria-hidden="true"
      src="/images/souhait/brand/mark-transparent.png"
      width={44}
      height={44}
    />
    <span className="wordmark__text">souha.it</span>
  </RouteLink>
);

const NavigationLabel: FC<{icon: IconName; label: string; detail: string}> = ({icon, label, detail}) => (
  <>
    <span className="topbar__nav-icon"><Icon name={icon} size={20} /></span>
    <span className="topbar__nav-copy">
      <span>{label}</span>
      <small aria-hidden="true">{detail}</small>
    </span>
    <Icon className="topbar__nav-arrow" name="arrow-right" size={17} />
  </>
);

const Topbar: FC<{
  navigate: (path: string) => void;
  variant: 'marketing' | 'public' | 'owner' | 'activity';
  onShare?: () => void;
  shareUrl?: string;
  copied?: boolean;
  listLabel?: string;
  ownerPublicPath?: string;
}> = ({
  navigate,
  variant,
  onShare,
  shareUrl,
  copied,
  listLabel = 'Liste',
  ownerPublicPath = '/julien',
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement | null>(null);
  const navigation = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const firstItem = navigation.current && navigation.current.querySelector<HTMLElement>('a, button');
    firstItem && firstItem.focus();
    const closeOutside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (navigation.current && !navigation.current.contains(target) && menuButton.current && !menuButton.current.contains(target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, [menuOpen]);
  return (
    <header className="topbar" onBlur={event => {
      if (menuOpen && !event.currentTarget.contains(event.relatedTarget as Node)) setMenuOpen(false);
    }} onKeyDown={event => {
      if (menuOpen && event.key === 'Escape') {
        event.preventDefault();
        setMenuOpen(false);
        menuButton.current && menuButton.current.focus();
      }
    }}>
      <Brand navigate={navigate} />
      <nav
        id="site-navigation"
        aria-label="Navigation principale"
        className={menuOpen ? 'topbar__nav is-open' : 'topbar__nav'}
        ref={navigation}
        onClick={event => {
          if ((event.target as HTMLElement).closest('a, button')) {
            setMenuOpen(false);
            menuButton.current && menuButton.current.focus();
          }
        }}
      >
        {variant === 'marketing' && (
          <>
            <RouteLink href="/julien" navigate={navigate}>
              <NavigationLabel icon="gift" label="Voir un exemple" detail="Une liste à découvrir" />
            </RouteLink>
            <a href="#how-it-works"><NavigationLabel icon="link" label="Comment ça marche" detail="Trois étapes, tout simplement" /></a>
            <RouteLink href="/pricing" navigate={navigate}>
              <NavigationLabel icon="sparkle" label="Tarifs" detail="Gratuit, Pass ou Plus" />
            </RouteLink>
          </>
        )}
        {variant === 'public' && (
          <>
            <a href="#wishes"><NavigationLabel icon="gift" label={listLabel} detail="Voir les idées de cadeaux" /></a>
            {onShare && (
              <button className="text-button" type="button" onClick={onShare}>
                <NavigationLabel icon={copied ? 'check' : 'share'} label={copied ? 'Lien copié' : 'Partager la liste'} detail={copied ? 'Prêt à envoyer à vos proches' : 'Copier le lien pour vos proches'} />
              </button>
            )}
            {shareUrl && (
              <a
                className="text-button"
                href={`https://wa.me/?text=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                onClick={() =>
                  trackEvent('list_shared', 'public', {channel: 'whatsapp'})
                }
              >
                <NavigationLabel icon="external" label="WhatsApp" detail="Ouvrir une conversation" />
              </a>
            )}
          </>
        )}
        {variant === 'owner' && (
          <>
            <a href="#my-wishes"><NavigationLabel icon="gift" label="Mes souhaits" detail="Ajouter et organiser vos idées" /></a>
            {ownerPublicPath && <a href="#gift-room"><NavigationLabel icon="lock" label="Suivi privé" detail="Suivre les cadeaux en toute discrétion" /></a>}
          </>
        )}
        {variant === 'activity' && (
          <>
            <RouteLink href="/me" navigate={navigate}><NavigationLabel icon="grid" label="Mes listes" detail="Retrouver votre espace privé" /></RouteLink>
            <RouteLink href="/pricing" navigate={navigate}><NavigationLabel icon="sparkle" label="Tarifs" detail="Gratuit, Pass ou Plus" /></RouteLink>
          </>
        )}
      </nav>
      {variant === 'owner' && !ownerPublicPath ? (
        <a className="add-button topbar__route" href="#my-wishes">Créer une liste <Icon name="plus" size={16} /></a>
      ) : <RouteLink
        className="add-button topbar__route"
        href={
          variant === 'owner'
            ? ownerPublicPath
            : variant === 'public'
            ? '/activity'
            : variant === 'activity'
            ? '/julien'
            : '/me'
        }
        navigate={navigate}
      >
        {variant === 'owner'
          ? 'Voir ma liste'
          : variant === 'public'
          ? 'Mon activité'
          : variant === 'activity'
          ? 'Voir une liste'
          : 'Créer ma liste'}
        {variant !== 'public' && <Icon name={variant === 'marketing' ? 'plus' : 'arrow-right'} size={16} />}
      </RouteLink>}
      <button
        className="menu-button"
        type="button"
        aria-label={menuOpen ? 'Fermer la navigation' : 'Ouvrir la navigation'}
        aria-expanded={menuOpen}
        aria-controls="site-navigation"
        ref={menuButton}
        onClick={() => setMenuOpen(value => !value)}
      >
        <Icon name={menuOpen ? 'close' : 'menu'} size={22} />
        <span className="menu-button__label">{menuOpen ? 'Fermer' : 'Menu'}</span>
      </button>
    </header>
  );
};

const MarketingPage: FC<{navigate: (path: string) => void}> = ({navigate}) => (
  <main className="souhait landing-page">
    <Topbar navigate={navigate} variant="marketing" />
    <section className="hero" id="top">
      <div className="hero__image" aria-hidden="true" />
      <div className="hero__content">
        <h1>
          Des cadeaux
          <br />
          <em>qui comptent.</em>
        </h1>
        <p className="hero__intro">
          Ajoutez un lien et dites pourquoi ce cadeau vous plaît. Vos proches
          peuvent le réserver ou participer ensemble, sans gâcher la surprise.
        </p>
        <div className="hero__actions">
          <RouteLink className="primary-button" href="/me" navigate={navigate}>
            Créer ma liste <Icon name="arrow-right" size={18} />
          </RouteLink>
          <a className="circle-link" href="#how-it-works" aria-label="Comment ça marche">
            <Icon name="arrow-down" size={20} />
          </a>
        </div>
      </div>
    </section>

    <section className="manifesto" aria-label="Ce qu’est souha.it">
      <div className="manifesto__mark" aria-hidden="true"><Motif name="heart" size={72} /></div>
      <h2>Une liste qui vous ressemble.</h2>
      <p className="manifesto__small">
        Tous les cadeaux peuvent entrer dans la même liste, quelle que soit la
        boutique où vous les avez trouvés.
      </p>
    </section>

    <section className="how" id="how-it-works">
      <div className="how__header">
        <h2>Ajoutez un souhait<br />en une minute.</h2>
        <RouteLink className="outline-button" href="/me" navigate={navigate}>
          Essayer gratuitement <Icon name="arrow-right" size={17} />
        </RouteLink>
      </div>
      <ol className="how__steps">
        <li>
          <div className="how__step-marker" aria-hidden="true">
            <span className="how__step-icon"><Icon name="link" size={24} /></span>
            <span className="how__step-number">01</span>
          </div>
          <div>
            <h3>Collez un lien.</h3>
            <p>
              Livre, meuble, billet ou expérience : si le cadeau a une adresse,
              il peut rejoindre votre liste.
            </p>
          </div>
        </li>
        <li>
          <div className="how__step-marker" aria-hidden="true">
            <span className="how__step-icon"><Icon name="heart" size={24} /></span>
            <span className="how__step-number">02</span>
          </div>
          <div>
            <h3>Dites pourquoi.</h3>
            <p>
              Une phrase suffit pour aider vos proches à comprendre ce qui vous
              ferait vraiment plaisir.
            </p>
          </div>
        </li>
        <li>
          <div className="how__step-marker" aria-hidden="true">
            <span className="how__step-icon"><Icon name="users" size={24} /></span>
            <span className="how__step-number">03</span>
          </div>
          <div>
            <h3>Laissez vos proches choisir.</h3>
            <p>
              Une personne peut réserver le cadeau. Plusieurs peuvent aussi
              promettre une participation avant de payer à l’extérieur.
            </p>
          </div>
        </li>
      </ol>
    </section>
    <SiteFooter navigate={navigate} />
  </main>
);

const CollectionStatusPage: FC<{
  navigate: (path: string) => void;
  state: 'loading' | 'missing' | 'unavailable' | 'missing-wish' | 'owner-unavailable';
  onRetry: () => void;
  listPath: string;
}> = ({navigate, state, onRetry, listPath}) => {
  const title = state === 'loading' ? 'Ouverture de la liste…'
    : state === 'missing' ? 'Cette liste est introuvable.'
    : state === 'missing-wish' ? 'Ce souhait n’est plus disponible.'
    : state === 'owner-unavailable' ? 'Votre espace ne peut pas être chargé.'
    : 'La liste ne peut pas être chargée.';
  useEffect(() => { document.title = `${title} | souha.it`; }, [title]);
  return (
    <main className="souhait collection-status-page">
      <Topbar navigate={navigate} variant="activity" />
      <section className="collection-status" role={state === 'loading' ? 'status' : undefined}>
        {state === 'loading' ? <span className="sui-spinner" aria-hidden="true" /> : <Motif name="gift" size={80} />}
        <h1>{title}</h1>
        {state !== 'loading' && <>
          <p>{state === 'missing'
            ? 'Vérifiez le lien reçu. La liste a peut-être été retirée ou son adresse a changé.'
            : state === 'missing-wish'
            ? 'Il a peut-être été retiré de la liste. Vous pouvez consulter les autres envies.'
            : 'Le service est momentanément indisponible. Réessayez dans quelques instants.'}</p>
          {state === 'unavailable' || state === 'owner-unavailable'
            ? <button className="primary-button" type="button" onClick={onRetry}><Icon name="refresh" size={18} /> Réessayer</button>
            : <RouteLink className="primary-button" href={state === 'missing-wish' ? listPath : '/'} navigate={navigate}>
                {state === 'missing-wish' ? 'Voir la liste' : 'Revenir à l’accueil'} <Icon name="arrow-right" size={18} />
              </RouteLink>}
        </>}
      </section>
    </main>
  );
};

const SiteFooter: FC<{navigate: (path: string) => void}> = ({navigate}) => (
  <footer className="footer">
    <p className="footer__line">
      Un beau cadeau
      <br />
      commence par <em>un souhait.</em>
    </p>
    <RouteLink className="footer__cta" href="/me" navigate={navigate}>
      <span>Créer ma liste</span>
      <Icon name="arrow-right" size={22} />
    </RouteLink>
    <div className="footer__bottom">
      <Brand navigate={navigate} light />
      <p>Pour offrir ensemble, simplement.</p>
      <p>
        <RouteLink href="/pricing" navigate={navigate}>Tarifs</RouteLink>
        {' · '}
        <RouteLink href="/privacy" navigate={navigate}>Confidentialité</RouteLink>
        {' · '}
        <RouteLink href="/terms" navigate={navigate}>Conditions</RouteLink>
      </p>
      <p>Paris · 2026</p>
    </div>
  </footer>
);

const WishGrid: FC<{
  wishes: Wish[];
  pledges: Pledge[];
  onSelect: (wish: Wish, opener: HTMLElement) => void;
  favoriteIds: ReadonlySet<string>;
  onToggleFavorite: (wishId: string) => void;
}> = ({wishes, pledges, onSelect, favoriteIds, onToggleFavorite}) => (
  <div className="wish-grid">
    {wishes.map((wish, index) => {
      const pledged =
        wish.pledged +
        pledges
          .filter(pledge => pledge.wishId === wish.id)
          .reduce((total, pledge) => total + pledge.amount, 0);
      const percentage = Math.min(100, Math.round((pledged / wish.price) * 100));
      const isFavorite = favoriteIds.has(wish.id);
      return (
        <article
          className={`wish ${wish.shape}`}
          key={wish.id}
          style={{'--wish-index': index} as React.CSSProperties}
        >
          <button
            className="wish__image-button"
            type="button"
            onClick={event => onSelect(wish, event.currentTarget)}
            aria-label={`Ouvrir ${wish.title}`}
          >
            <img
              src={directionSixImage(wish.image)}
              alt={wish.title}
              width={800}
              height={600}
              loading={index < 2 ? 'eager' : 'lazy'}
              onError={event => {
                event.currentTarget.src =
                  '/images/souhait/illustrations/wish-fallback.webp';
              }}
            />
            <span className={`wish__status${wish.isReserved || wish.myReservationId ? ' wish__status--reserved' : pledged > 0 || wish.myContributionId ? ' wish__status--shared' : ''}`}>
              <Icon name={wish.isReserved || wish.myReservationId ? 'check' : pledged > 0 || wish.myContributionId ? 'users' : 'sparkle'} size={12} />
              {wish.myContributionId
                ? 'Votre participation'
                : wish.myReservationId
                ? 'Réservé par vous'
                : wish.isReserved
                ? 'Réservé'
                : wish.status || (pledged > 0 ? 'Populaire' : 'Nouveau')}
            </span>
            <span className="wish__reveal">Voir le souhait <Icon name="external" size={14} /></span>
          </button>
          <button
            className={`wish__favorite${isFavorite ? ' is-active' : ''}`}
            type="button"
            aria-label={
              isFavorite
                ? `Retirer ${wish.title} des favoris`
                : `Ajouter ${wish.title} aux favoris`
            }
            aria-pressed={isFavorite}
            onClick={() => onToggleFavorite(wish.id)}
          >
            <Icon name="heart" size={19} filled={isFavorite} />
          </button>
          <div className="wish__body">
            <p className="wish__category">{categoryForWish(wish)}</p>
            <h3>
              <button type="button" onClick={event => onSelect(wish, event.currentTarget)}>
                {wish.title}
              </button>
            </h3>
            {wish.note && <p className="wish__note">{wish.note}</p>}
            <strong className="wish__price">{formatCurrency(wish.price)}</strong>
            <div className="wish__footer">
              {pledged > 0 && (
                <>
                  <div
                    className="mini-progress"
                    aria-label={`${percentage}% promis`}
                  >
                    <span style={{width: `${percentage}%`}} />
                  </div>
                  <div className="wish__pledged">
                    <Icon name="users" size={15} />
                    <span>{formatCurrency(pledged)} promis</span>
                    <strong>{percentage}%</strong>
                  </div>
                </>
              )}
              {wish.isReserved ? (
                <span className="wish__reserved"><Icon name="check" size={15} /> Réservé</span>
              ) : wish.mode === 'gift' ? (
                <span className="wish__mode"><Icon name="gift" size={15} /> À réserver</span>
              ) : pledged === 0 ? (
                <span className="wish__mode"><Icon name="users" size={15} /> À offrir ensemble</span>
              ) : null}
            </div>
            {wish.deadline && (
              <p className="wish__deadline">À organiser avant le {formatDeadline(wish.deadline)}</p>
            )}
          </div>
        </article>
      );
    })}
  </div>
);

const PublicListPage: FC<{
  navigate: (path: string) => void;
  wishlist: ApiWishlist | null;
  slug: string;
  wishes: Wish[];
  pledges: Pledge[];
  captains: string[];
  setPledges: React.Dispatch<React.SetStateAction<Pledge[]>>;
  setCaptains: React.Dispatch<React.SetStateAction<string[]>>;
  onRefresh: () => Promise<void>;
  cloudMode: boolean;
  serviceUnavailable: boolean;
  selectedWishId?: string;
}> = ({
  navigate,
  wishlist,
  slug,
  wishes,
  pledges,
  captains,
  setPledges,
  setCaptains,
  onRefresh,
  cloudMode,
  serviceUnavailable,
  selectedWishId,
}) => {
  const [selected, setSelected] = useState<Wish | null>(null);
  const [copied, setCopied] = useState(false);
  const [shareError, setShareError] = useState('');
  const [sort, setSort] = useState('position');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('Tout');
  const [favorites, setFavorites] = useState<string[]>(() => {
    const stored = safeRead<unknown>(`souhait:favorites:v1:${slug}`, []);
    return Array.isArray(stored) ? stored.filter(value => typeof value === 'string') : [];
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(`souhait:favorites:v1:${slug}`, JSON.stringify(favorites));
    } catch {
      // Browsing and filtering remain available when storage is blocked.
    }
  }, [favorites, slug]);
  const favoriteIds = useMemo(() => new Set(favorites), [favorites]);
  const availableCategories = useMemo(() => catalogCategories
    .map(item => ({...item, count: wishes.filter(wish => matchesCatalogCategory(wish, item.label)).length}))
    .filter(item => item.label === 'Tout' || item.count > 0), [wishes]);
  const wishOpener = useRef<HTMLElement | null>(null);
  const lastTrackedWish = useRef('');
  const totalPrice = wishes.reduce((total, wish) => total + wish.price, 0);
  const totalPledged = wishes.reduce(
    (total, wish) => total + Math.min(wish.price, wish.pledged),
    0
  );
  const sortedWishes = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const copy = wishes.filter(wish => {
      const wishCategory = categoryForWish(wish);
      const matchesSearch =
        !normalizedSearch ||
        `${wish.title} ${wish.merchant} ${wish.note} ${wishCategory}`
          .toLowerCase()
          .includes(normalizedSearch);
      const matchesCategory = matchesCatalogCategory(wish, category);
      return matchesSearch && matchesCategory;
    });
    if (sort === 'price-asc') return copy.sort((a, b) => a.price - b.price);
    if (sort === 'price-desc') return copy.sort((a, b) => b.price - a.price);
    if (sort === 'name-asc')
      return copy.sort((a, b) => a.title.localeCompare(b.title));
    if (sort === 'name-desc')
      return copy.sort((a, b) => b.title.localeCompare(a.title));
    if (sort === 'store')
      return copy.sort((a, b) => a.merchant.localeCompare(b.merchant));
    if (sort === 'type')
      return copy.sort((a, b) => a.mode.localeCompare(b.mode));
    return copy;
  }, [category, search, sort, wishes]);
  const promiseWish = useMemo(() => {
    let mostPledged: Wish | undefined;
    for (const wish of wishes) {
      if (wish.mode === 'gift') continue;
      if (!mostPledged || wish.pledged > mostPledged.pledged) {
        mostPledged = wish;
      }
    }
    return mostPledged;
  }, [wishes]);
  const displayedWishes =
    category === 'Favoris'
      ? sortedWishes.filter(wish => favoriteIds.has(wish.id))
      : sortedWishes;
  useEffect(() => {
    document.title = `${wishlist ? wishlist.title : 'Liste de souhaits'} | souha.it`;
  }, [wishlist]);
  useEffect(() => {
    if (!selected) return;
    const updated = wishes.find(wish => wish.id === selected.id);
    if (updated && updated !== selected) setSelected(updated);
  }, [selected, wishes]);
  useEffect(() => {
    if (!selected || lastTrackedWish.current === selected.id) return;
    lastTrackedWish.current = selected.id;
    trackEvent('wish_opened', 'public', {mode: selected.mode});
  }, [selected]);
  useEffect(() => {
    if (!selectedWishId) {
      setSelected(null);
      return;
    }
    const deepLinkedWish = wishes.find(wish => wish.id === selectedWishId);
    if (deepLinkedWish) setSelected(deepLinkedWish);
  }, [selectedWishId, wishes]);
  const openWish = (wish: Wish, opener?: HTMLElement) => {
    if (opener) wishOpener.current = opener;
    window.history.pushState(
      {},
      '',
      `/${encodeURIComponent(slug)}/${encodeURIComponent(wish.id)}`
    );
    window.dispatchEvent(new PopStateEvent('popstate'));
    setSelected(wish);
  };
  const closeWish = () => {
    window.history.pushState({}, '', `/${encodeURIComponent(slug)}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
    setSelected(null);
    const opener = wishOpener.current;
    wishOpener.current = null;
    window.setTimeout(() => opener && opener.focus(), 0);
  };
  const selectedIndex = selected
    ? sortedWishes.findIndex(wish => wish.id === selected.id)
    : -1;
  const selectRelativeWish = (offset: number) => {
    if (selectedIndex < 0) return;
    const nextIndex =
      (selectedIndex + offset + sortedWishes.length) % sortedWishes.length;
    const nextWish = sortedWishes[nextIndex];
    window.history.replaceState(
      {},
      '',
      `/${encodeURIComponent(slug)}/${encodeURIComponent(nextWish.id)}`
    );
    window.dispatchEvent(new PopStateEvent('popstate'));
    setSelected(nextWish);
  };
  const copyList = async () => {
    setShareError('');
    try {
      if (!navigator.clipboard) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(
        `${window.location.origin}/${encodeURIComponent(slug)}`
      );
      setCopied(true);
      trackEvent('list_shared', 'public', {channel: 'copy'});
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setShareError('La copie n’a pas fonctionné. Vous pouvez copier l’adresse de cette page.');
    }
  };

  return (
    <main className="souhait public-page">
      <Topbar
        navigate={navigate}
        variant="public"
        onShare={copyList}
        shareUrl={`${window.location.origin}/${encodeURIComponent(slug)}`}
        copied={copied}
        listLabel={wishlist ? wishlist.title : 'Liste'}
      />
      {shareError && <div className="service-notice" role="alert">{shareError}</div>}
      {serviceUnavailable && !DEMO_MODE && (
        <div className="service-notice" role="status">
          Les actions sont momentanément indisponibles. Vous pouvez consulter
          la liste, mais les réservations et participations sont en pause.
          <button type="button" onClick={() => onRefresh()}><Icon name="refresh" size={16} /> Réessayer</button>
        </div>
      )}
      <section className="public-hero">
        <div className="public-hero__portrait" aria-hidden="true" />
        <div className="public-hero__copy">
          {wishlist && wishlist.occasion && (
            <p className="public-hero__occasion"><Icon name="gift" size={17} />{occasionLabel(wishlist.occasion)}</p>
          )}
          <h1>{wishlist ? wishlist.title : 'Une liste choisie avec soin'}</h1>
          <p className="public-hero__subtitle">
            {wishlist && wishlist.subtitle
              ? wishlist.subtitle
              : 'Des cadeaux qui font vraiment plaisir.'}
          </p>
          <p className="public-hero__description">
            {wishlist && wishlist.description
              ? wishlist.description
              : 'Des idées simples, choisies avec soin.'}
          </p>
          <a href="#wishes">Voir les souhaits <Icon name="arrow-down" size={18} /></a>
          <div className="public-hero__note">
            <span><Icon name="gift" size={15} />{wishes.length} souhait{wishes.length === 1 ? '' : 's'}</span>
            <span><Icon name="users" size={15} />
              {totalPrice
                ? `${Math.round((totalPledged / totalPrice) * 100)} % déjà promis`
                : 'Gardez la surprise'}
            </span>
          </div>
        </div>
      </section>
      <section className="collection public-collection" id="wishes">
        <div className="collection__heading">
          <div>
            <h2>Les souhaits,<br />choisis avec soin.</h2>
          </div>
          <p>
            Réservez un cadeau ou participez à plusieurs. Les noms et les
            montants individuels restent privés.
          </p>
        </div>
        <div className="catalog-tools">
          <label className="catalog-search">
            <Icon name="search" size={20} />
            <input
              type="search"
              value={search}
              placeholder="Rechercher un souhait…"
              aria-label="Rechercher dans les souhaits"
              onChange={event => setSearch(event.target.value)}
            />
          </label>
          <button
            className={`catalog-action catalog-action--favorites${
              category === 'Favoris' ? ' is-active' : ''
            }`}
            type="button"
            aria-label="Afficher les favoris"
            aria-pressed={category === 'Favoris'}
            title="Afficher les favoris"
            onClick={() =>
              setCategory(current =>
                current === 'Favoris' ? 'Tout' : 'Favoris'
              )
            }
          >
            <Icon name="heart" size={20} filled={category === 'Favoris'} />
            <span className="catalog-action__label">Favoris</span>
            {favorites.length > 0 && <span>{favorites.length}</span>}
          </button>
          <RouteLink
            className="catalog-action"
            href="/activity"
            ariaLabel="Mon activité"
            navigate={navigate}
          >
            <Icon name="bell" size={20} />
          </RouteLink>
          {promiseWish && <a
            className="catalog-action catalog-action--gift"
            href="#promise-meter"
            aria-label="Voir la participation en cours"
            title="Voir la participation en cours"
          >
            <Icon name="gift" size={20} />
            {promiseWish.pledgers > 0 && <span>{promiseWish.pledgers}</span>}
          </a>}
        </div>
        <div className="catalog-categories" role="group" aria-label="Parcourir les catégories">
          {availableCategories.map(item => (
            <button
              className={category === item.label ? 'is-active' : ''}
              type="button"
              key={item.label}
              aria-pressed={category === item.label}
              onClick={() => setCategory(item.label)}
            >
              <span className={`catalog-category-icon catalog-category-icon--${item.tone}`} aria-hidden="true"><Icon name={item.icon} size={19} /></span>
              <small>{item.label}</small>
              <span className="catalog-category-count" aria-hidden="true">{item.count}</span>
            </button>
          ))}
        </div>
        <div className="catalog-summary">
          <p aria-live="polite">
            <strong>{displayedWishes.length}</strong> souhait{displayedWishes.length === 1 ? '' : 's'}
            {displayedWishes.length !== wishes.length && <> sur {wishes.length}</>}
          </p>
          <div className="collection-sort">
              <label htmlFor="wishlist-sort">Trier</label>
              <Select
                id="wishlist-sort"
                aria-label="Trier les souhaits"
                value={sort}
                onValueChange={setSort}
                options={[
                  {value: 'position', label: 'Ordre de la liste'},
                  {value: 'price-asc', label: 'Prix · croissant'},
                  {value: 'price-desc', label: 'Prix · décroissant'},
                  {value: 'name-asc', label: 'Nom · A à Z'},
                  {value: 'name-desc', label: 'Nom · Z à A'},
                  {value: 'store', label: 'Boutique'},
                  {value: 'type', label: 'Type de cadeau'},
                ]}
              />
          </div>
        </div>
        <div className={`catalog-layout${promiseWish ? '' : ' catalog-layout--full'}`}>
          <div className="catalog-results">
            {displayedWishes.length ? (
              <WishGrid
                wishes={displayedWishes}
                pledges={pledges}
                onSelect={openWish}
                favoriteIds={favoriteIds}
                onToggleFavorite={wishId =>
                  setFavorites(current =>
                    current.includes(wishId)
                      ? current.filter(id => id !== wishId)
                      : [...current, wishId]
                  )
                }
              />
            ) : (
              <div className="catalog-empty">
                <span aria-hidden="true"><Icon name={category === 'Favoris' ? 'heart' : 'search'} size={28} /></span>
                <h3>{!wishes.length ? 'Les premiers souhaits arrivent bientôt.' : category === 'Favoris' ? 'Vos coups de cœur, ici.' : 'Aucun souhait trouvé.'}</h3>
                <p>{!wishes.length
                  ? 'Revenez un peu plus tard pour découvrir les envies ajoutées à cette liste.'
                  : category === 'Favoris' && !search
                  ? 'Ajoutez un souhait avec son cœur pour le retrouver ici.'
                  : 'Essayez un autre mot ou une autre catégorie.'}</p>
                {wishes.length > 0 && <button
                  type="button"
                  onClick={() => {
                    setSearch('');
                    setCategory('Tout');
                  }}
                >
                  Afficher tous les souhaits
                </button>}
              </div>
            )}
          </div>
          {promiseWish && (
            <aside className="promise-meter" id="promise-meter">
              <div className="promise-meter__heading"><Icon name="users" size={18} /><p>À offrir à plusieurs</p></div>
              <strong>{promiseWish.title}</strong>
              <div className="promise-meter__amount">
                <b>{formatCurrency(promiseWish.pledged)}</b>
                <span>/ {formatCurrency(promiseWish.price)}</span>
              </div>
              <div className="promise-meter__bar" role="progressbar" aria-label="Montant promis" aria-valuemin={0} aria-valuemax={promiseWish.price} aria-valuenow={Math.min(promiseWish.pledged, promiseWish.price)}>
                <span
                  style={{
                    width: `${Math.min(
                      100,
                      Math.round(
                        (promiseWish.pledged / promiseWish.price) * 100
                      )
                    )}%`,
                  }}
                />
              </div>
              <small>{promiseWish.pledgers} proche{promiseWish.pledgers === 1 ? '' : 's'} participe{promiseWish.pledgers === 1 ? '' : 'nt'}</small>
              <p className="promise-meter__hint">Un petit geste de chacun pour une belle envie.</p>
              <button
                type="button"
                onClick={event => openWish(promiseWish, event.currentTarget)}
              >
                Participer <Icon name="arrow-right" size={17} />
              </button>
              <Motif name="gift" size={64} className="promise-meter__motif" />
            </aside>
          )}
        </div>
        <div className="catalog-reassurance">
          <div><span><Icon name="link" size={21} /></span><p><b>Un lien suffit</b><small>Tout est réuni ici</small></p></div>
          <div><span><Icon name="lock" size={21} /></span><p><b>Surprise préservée</b><small>Les participations sont privées</small></p></div>
          <div><span><Icon name="refresh" size={21} /></span><p><b>Vous gardez le choix</b><small>Modifiable à tout moment</small></p></div>
          <div><span><Icon name="users" size={21} /></span><p><b>À votre façon</b><small>Seul ou à plusieurs</small></p></div>
        </div>
      </section>
      <footer className="public-footer">
        <div><Icon name="heart" size={28} /><p>À chacun sa façon de faire plaisir.<small>{wishlist ? wishlist.title : 'Une liste choisie avec soin'}</small></p></div>
        <RouteLink href="/me" navigate={navigate}>
          Créer ma propre liste <Icon name="arrow-right" size={18} />
        </RouteLink>
      </footer>
      {selected && (
        <WishDetail
          key={selected.id}
          wish={selected}
          pledges={pledges}
          captains={captains}
          onPrevious={() => selectRelativeWish(-1)}
          onNext={() => selectRelativeWish(1)}
          onClose={closeWish}
          onPledge={async pledge => {
            if (!cloudMode) {
              if (!DEMO_MODE) {
                throw new Error(
                  'Les participations sont momentanément indisponibles. Réessayez bientôt.'
                );
              }
              setPledges(current => [...current, pledge]);
              return;
            }
            const session = await getSession();
            let recoveryCode = '';
            if (!session.contributor) {
              const created = await createSession(
                pledge.name,
                pledge.email,
                pledge.turnstileToken
              );
              recoveryCode = created.recoveryCode;
            }
            await pledgeToWish(
              pledge.wishId,
              Math.round(pledge.amount * 100),
              pledge.message,
              pledge.recipientMessage,
              pledge.coordinationOptIn,
              pledge.email
            );
            await onRefresh();
            return recoveryCode;
          }}
          onReserve={async (name, email, privateMessage, turnstileToken) => {
            if (!cloudMode) {
              if (!DEMO_MODE) {
                throw new Error(
                  'Les réservations sont momentanément indisponibles. Réessayez bientôt.'
                );
              }
              return;
            }
            const session = await getSession();
            let recoveryCode = '';
            if (!session.contributor) {
              const created = await createSession(
                name,
                email,
                turnstileToken
              );
              recoveryCode = created.recoveryCode;
            }
            await reserveWish(selected.id, privateMessage);
            await onRefresh();
            return recoveryCode;
          }}
          onCaptain={async (name, email, turnstileToken) => {
            if (cloudMode) {
              const session = await getSession();
              let recoveryCode = '';
              if (!session.contributor) {
                const created = await createSession(name, email, turnstileToken);
                recoveryCode = created.recoveryCode;
              }
              await becomeCaptain(selected.id);
              await onRefresh();
              return recoveryCode;
            }
            setCaptains(current =>
              current.includes(selected.id) ? current : [...current, selected.id]
            );
            return;
          }}
          onRevealMessages={async () => {
            if (!cloudMode) {
              throw new Error('Le suivi des cadeaux est momentanément indisponible.');
            }
            await revealGiftMessages(selected.id);
            await onRefresh();
          }}
          ownerLabel={
            (wishlist && (wishlist.people || wishlist.title)) ||
            'Le propriétaire de la liste'
          }
          paymentUrl={(wishlist && wishlist.paymentUrl) || ''}
          paymentLabel={(wishlist && wishlist.paymentLabel) || ''}
        />
      )}
    </main>
  );
};

const CaptainUpdateForm: FC<{item: ActivityItem}> = ({item}) => {
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('');
  const [sending, setSending] = useState(false);
  if (item.status !== 'active') return null;
  return (
    <form
      className="captain-update"
      onSubmit={async event => {
        event.preventDefault();
        setSending(true);
        setStatus('');
        try {
          const result = await sendCaptainUpdate(item.wishId, message);
          setMessage('');
          setStatus(
            result.recipientCount
              ? `Message envoyé à ${result.recipientCount} personne${
                  result.recipientCount === 1 ? '' : 's'
                } ayant accepté les e-mails.`
              : 'Message enregistré. Personne n’a encore accepté les e-mails.'
          );
        } catch (error) {
          setStatus(
            error instanceof Error
              ? error.message
              : 'Le message n’a pas pu être envoyé.'
          );
        } finally {
          setSending(false);
        }
      }}
    >
      <label htmlFor={`captain-update-${item.wishId}`}>
        Message privé aux participants qui ont accepté les e-mails
      </label>
      <textarea
        id={`captain-update-${item.wishId}`}
        value={message}
        onChange={event => setMessage(event.target.value)}
        minLength={5}
        maxLength={500}
        placeholder="Disponibilité, calendrier ou prochaine étape…"
        required
      />
      <div>
        <small>{message.length}/500</small>
        <button type="submit" disabled={sending || message.trim().length < 5}>
          {sending ? 'Envoi…' : 'Envoyer le message'}
        </button>
      </div>
      {status && <p role="status">{status}</p>}
    </form>
  );
};

const ContributorActivityPage: FC<{
  navigate: (path: string) => void;
}> = ({navigate}) => {
  const [contributor, setContributor] = useState<ApiContributor | null>(null);
  const [contributions, setContributions] = useState<ActivityItem[]>([]);
  const [reservations, setReservations] = useState<ActivityItem[]>([]);
  const [captainships, setCaptainships] = useState<ActivityItem[]>([]);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [recoveryTurnstileToken, setRecoveryTurnstileToken] = useState('');
  const [recoveryTurnstileAttempt, setRecoveryTurnstileAttempt] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [pendingAction, setPendingAction] = useState('');
  const [recovering, setRecovering] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const activity = await loadActivity();
      setContributor(activity.contributor);
      setContributions(activity.contributions);
      setReservations(activity.reservations);
      setCaptainships(activity.captainships);
      setErrorMessage('');
      setLoadError('');
    } catch (error) {
      if (error instanceof SouhaitApiError && error.status === 401) {
        setContributor(null);
        setContributions([]);
        setReservations([]);
        setCaptainships([]);
        setLoadError('');
      } else {
        setLoadError('Votre activité n’a pas pu être chargée. Réessayez dans un instant.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'Mon activité | souha.it';
    refresh(true);
  }, [refresh]);

  const runAction = async (key: string, action: () => Promise<unknown>) => {
    if (pendingAction) return;
    setPendingAction(key);
    setActionError('');
    try {
      await action();
      await refresh();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Cette action n’a pas pu être enregistrée. Réessayez.');
    } finally {
      setPendingAction('');
    }
  };

  const activityRow = (
    item: ActivityItem,
    kind: 'contribution' | 'reservation'
  ) => (
    <article className="activity-row" key={`${kind}-${item.id}`}>
      <div>
        <p className="activity-kind">
          <Icon name={kind === 'contribution' ? 'users' : 'gift'} size={15} />
          {kind === 'contribution' ? 'Participation' : 'Réservation'}
        </p>
        <h3>{item.title}</h3>
        <span>
          {item.amountCents
            ? `${formatCurrency(item.amountCents / 100)} · `
            : ''}
          {activityStatusLabel(item.status)}
        </span>
        {item.privateMessage && <p>{item.privateMessage}</p>}
      </div>
      <div className="activity-row__actions">
        <RouteLink href={`/${encodeURIComponent(item.slug)}/${encodeURIComponent(item.wishId)}`} navigate={navigate}>
          Voir le souhait
        </RouteLink>
        {kind === 'contribution' && item.status === 'pledged' && (
          <>
            {safeExternalUrl(item.paymentUrl || '') && (
              <a
                href={safeExternalUrl(item.paymentUrl || '')}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  const provider = paymentProviderCategory(item.paymentUrl || '');
                  trackEvent('payment_handoff_opened', 'activity', {
                    provider,
                  });
                }}
              >
                {item.paymentLabel || 'Continuer vers le paiement'} <Icon name="external" size={15} />
              </a>
            )}
            <button
              type="button"
              disabled={Boolean(pendingAction)}
              onClick={() => runAction(`settle-${item.id}`, () => updateContribution(item.wishId, item.id, 'settle'))}
            >
              {pendingAction === `settle-${item.id}` ? 'Enregistrement…' : 'Marquer comme payé'}
            </button>
            <button
              type="button"
              disabled={Boolean(pendingAction)}
              onClick={() => runAction(`cancel-${item.id}`, () => updateContribution(item.wishId, item.id, 'cancel'))}
            >
              {pendingAction === `cancel-${item.id}` ? 'Annulation…' : 'Annuler'}
            </button>
          </>
        )}
        {kind === 'reservation' && item.status !== 'cancelled' && (
          <>
            {item.status === 'pending' && (
              <button
                type="button"
                disabled={Boolean(pendingAction)}
                onClick={() => runAction(`confirm-${item.id}`, () => updateReservation(item.wishId, item.id, 'confirm'))}
              >
                {pendingAction === `confirm-${item.id}` ? 'Confirmation…' : 'Confirmer l’achat'}
              </button>
            )}
            <button
              type="button"
              disabled={Boolean(pendingAction)}
              onClick={() => runAction(`release-${item.id}`, () => updateReservation(item.wishId, item.id, 'cancel'))}
            >
              {pendingAction === `release-${item.id}` ? 'Annulation…' : 'Libérer le cadeau'}
            </button>
          </>
        )}
      </div>
    </article>
  );

  return (
    <main className="souhait activity-page">
      <Topbar navigate={navigate} variant="activity" />
      <section className="activity-hero">
        <p className="privacy-note"><Icon name="lock" size={17} />Visible seulement par vous</p>
        <h1>Vos cadeaux<br /><em>en cours.</em></h1>
        <p>
          Confirmez ou annulez vos réservations et participations. Le
          propriétaire de la liste ne voit jamais votre nom ni votre montant.
        </p>
      </section>
      {(loadError || actionError) && (
        <div className="activity-feedback">
          <p className="form-error" role="alert">{loadError || actionError}</p>
          {loadError && <button type="button" disabled={loading} onClick={() => refresh(true)}>Réessayer</button>}
        </div>
      )}
      {loading ? (
        <p className="activity-empty">Chargement de votre activité…</p>
      ) : contributor ? (
        <section className="activity-list">
          <header>
            <div>
              <p className="activity-identity">Identité enregistrée sur cet appareil</p>
              <h2>{contributor.displayName}</h2>
            </div>
            <button
              type="button"
              disabled={Boolean(pendingAction)}
              onClick={() => runAction('logout', () => logout())}
            >
              {pendingAction === 'logout' ? 'Déconnexion…' : 'Se déconnecter'}
            </button>
          </header>
          {contributions.map(item => activityRow(item, 'contribution'))}
          {reservations.map(item => activityRow(item, 'reservation'))}
          {captainships.map(item => (
            <article className="activity-row" key={`captain-${item.wishId}`}>
              <div>
                <p className="activity-kind"><Icon name="users" size={15} />Capitaine du cadeau</p>
                <h3>{item.title}</h3>
                <span>{activityStatusLabel(item.status)}</span>
                <CaptainUpdateForm item={item} />
              </div>
              <RouteLink href={`/${encodeURIComponent(item.slug)}/${encodeURIComponent(item.wishId)}`} navigate={navigate}>
                Coordonner
              </RouteLink>
            </article>
          ))}
          {!contributions.length &&
            !reservations.length &&
            !captainships.length && (
              <p className="activity-empty">Aucune activité pour le moment.</p>
            )}
        </section>
      ) : !loadError ? (
        <section className="activity-recovery">
          <span className="activity-recovery__icon" aria-hidden="true"><Icon name="refresh" size={23} /></span>
          <h2>Retrouver votre activité privée</h2>
          <p>
            Saisissez le code affiché lors de votre première action. Aucune
            adresse e-mail n’est nécessaire.
          </p>
          <form
            onSubmit={async event => {
              event.preventDefault();
              if (recovering || !recoveryTurnstileToken) return;
              setRecovering(true);
              setErrorMessage('');
              try {
                await recoverSession(recoveryCode, recoveryTurnstileToken);
                await refresh();
              } catch (error) {
                setRecoveryTurnstileToken('');
                setRecoveryTurnstileAttempt(current => current + 1);
                setErrorMessage(
                  error instanceof Error
                    ? error.message
                  : 'Ce code n’a pas pu être vérifié.'
                );
              } finally {
                setRecovering(false);
              }
            }}
          >
            <label htmlFor="activity-recovery-code">Code de récupération</label>
            <input
              id="activity-recovery-code"
              value={recoveryCode}
              onChange={event => setRecoveryCode(event.target.value)}
              placeholder="XXXX-XXXX-XXXX"
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              required
            />
            <TurnstileWidget
              key={recoveryTurnstileAttempt}
              onToken={setRecoveryTurnstileToken}
            />
            <button className="primary-button" type="submit" disabled={recovering || !recoveryCode.trim() || !recoveryTurnstileToken}>
              {recovering ? 'Récupération…' : 'Retrouver mon activité'} <Icon name="arrow-right" size={17} />
            </button>
          </form>
          {errorMessage && (
            <p className="form-error" role="alert">
              {errorMessage}
            </p>
          )}
        </section>
      ) : null}
    </main>
  );
};

const OwnerPage: FC<{
  navigate: (path: string) => void;
  account: ApiOwnerAccount;
  ownerLists: ApiOwnerWishlist[];
  currentSlug: string;
  wishes: Wish[];
  onAddWish: (wish: Wish) => Promise<void>;
  onUpdateWish: (wish: Wish) => Promise<void>;
  onArchiveWish: (wish: Wish) => Promise<void>;
  onCreateList: (input: {
    slug: string;
    title: string;
    subtitle: string;
    occasion: string;
  }) => Promise<void>;
  onLogout: () => Promise<void>;
  onBillingPortal: () => Promise<void>;
  onUpdatePayment: (
    slug: string,
    paymentUrl: string,
    paymentLabel: string
  ) => Promise<void>;
  onRefreshAccount: () => Promise<void>;
}> = ({
  navigate,
  account,
  ownerLists,
  currentSlug,
  wishes,
  onAddWish,
  onUpdateWish,
  onArchiveWish,
  onCreateList,
  onLogout,
  onBillingPortal,
  onUpdatePayment,
  onRefreshAccount,
}) => {
  const [addOpen, setAddOpen] = useState(false);
  const [editingWish, setEditingWish] = useState<Wish | null>(null);
  const [newListOpen, setNewListOpen] = useState(ownerLists.length === 0);
  const [newListTitle, setNewListTitle] = useState('');
  const [newListSlug, setNewListSlug] = useState('');
  const [newListSlugEdited, setNewListSlugEdited] = useState(false);
  const [newListSubtitle, setNewListSubtitle] = useState('');
  const [newListOccasion, setNewListOccasion] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get('occasion');
    return occasionPresets.some(preset => preset.value === requested) ? requested || '' : '';
  });
  const [newListTitleEdited, setNewListTitleEdited] = useState(false);
  const [newListSubtitleEdited, setNewListSubtitleEdited] = useState(false);
  const [newListError, setNewListError] = useState('');
  const [creatingList, setCreatingList] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  const activeList = ownerLists.find(list => list.slug === currentSlug);
  const [paymentUrl, setPaymentUrl] = useState(
    activeList ? activeList.paymentUrl : ''
  );
  const [paymentLabel, setPaymentLabel] = useState(
    activeList ? activeList.paymentLabel : ''
  );
  const [paymentError, setPaymentError] = useState('');
  const [savingPayment, setSavingPayment] = useState(false);
  const [openingBilling, setOpeningBilling] = useState(false);
  const atListLimit =
    account.listLimit !== null && ownerLists.length >= account.listLimit;
  const atWishLimit = Boolean(activeList && activeList.wishLimit !== null && activeList.wishCount >= activeList.wishLimit);
  useEffect(() => {
    if (atListLimit) setNewListOpen(false);
  }, [atListLimit]);
  const openOffers = (trigger: string) => {
    trackEvent('upgrade_viewed', 'owner', {trigger});
    navigate(`/pricing${activeList ? `?list=${encodeURIComponent(activeList.id)}` : ''}`);
  };
  const addWish = () => {
    setSavedMessage('');
    if (atWishLimit) openOffers('wish_limit');
    else setAddOpen(true);
  };
  useEffect(() => {
    setPaymentUrl(activeList ? activeList.paymentUrl : '');
    setPaymentLabel(activeList ? activeList.paymentLabel : '');
    setPaymentError('');
  }, [activeList]);
  useEffect(() => {
    document.title = activeList
      ? `${activeList.title} · Mes listes | souha.it`
      : 'Mes listes | souha.it';
  }, [activeList]);
  return (
    <main className="souhait owner-page">
      <Topbar
        navigate={navigate}
        variant="owner"
        ownerPublicPath={
          currentSlug ? `/${encodeURIComponent(currentSlug)}` : ''
        }
      />
      <section className="owner-account-bar" aria-label="Compte et offre">
        <div>
          <span className={`owner-plan owner-plan--${account.plan}`}>
            {account.plan === 'plus'
              ? 'Plus'
              : activeList && activeList.plan === 'pass'
              ? 'Pass occasion'
              : 'Gratuit'}
          </span>
          <small>{account.email}</small>
          {activeList && activeList.plan === 'pass' && (
            <small>Pass jusqu’au {formatAccessDate(activeList.passExpiresAt)}</small>
          )}
          {account.plan === 'plus' && account.subscriptionPeriodEnd && (
            <small>
              {account.subscriptionStatus === 'cancelled' ? 'Plus annulé, actif jusqu’au ' : 'Plus actif jusqu’au '}
              {formatAccessDate(account.subscriptionPeriodEnd)}
            </small>
          )}
        </div>
        <div>
          <button type="button" onClick={() => openOffers('pricing')}>
            Voir les offres
          </button>
          {account.subscriptionStatus && (
            <button
              type="button"
              disabled={openingBilling}
              onClick={async () => {
                setOpeningBilling(true);
                try {
                  await onBillingPortal();
                } catch (error) {
                  setSavedMessage(error instanceof Error ? error.message : 'La facturation est momentanément indisponible.');
                } finally {
                  setOpeningBilling(false);
                }
              }}
            >
              {openingBilling ? 'Ouverture…' : 'Gérer la facturation'}
            </button>
          )}
          <button type="button" disabled={loggingOut} onClick={async () => {
            setLoggingOut(true);
            try {
              await onLogout();
            } catch (error) {
              setSavedMessage(error instanceof Error ? error.message : 'La déconnexion n’a pas pu être enregistrée. Réessayez.');
            } finally {
              setLoggingOut(false);
            }
          }}>{loggingOut ? 'Déconnexion…' : 'Se déconnecter'}</button>
        </div>
      </section>
      <BillingReturnNotice account={account} wishlists={ownerLists} onRefresh={onRefreshAccount} />
      <section className="owner-intro">
        <div>
          <p className="privacy-note"><Icon name="lock" size={17} />Votre espace privé</p>
          <h1>Vos listes,<br /><em>au même endroit.</em></h1>
        </div>
        {activeList && <button
          className="owner-add"
          type="button"
          disabled={!activeList}
          onClick={() => activeList && addWish()}
        >
          <Icon name="plus" size={20} />
          <b>Ajouter un souhait</b>
        </button>}
      </section>

      <section className="owner-workspace" id="my-wishes">
        <aside className="owner-rail">
          <h2 className="owner-rail__heading">Vos listes</h2>
          {ownerLists.map(list => (
            <button
              className={list.slug === currentSlug ? 'is-active' : ''}
              type="button"
              key={list.id}
              onClick={() => navigate(`/me/${encodeURIComponent(list.slug)}`)}
            >
              {list.title} <span>{list.wishCount}</span>
            </button>
          ))}
          {atListLimit ? (
            <button
              className="owner-rail__new"
              type="button"
              onClick={() => openOffers('list_limit')}
            >
              Plusieurs listes avec Plus <Icon name="arrow-right" size={16} />
            </button>
          ) : (
            <button
              className="owner-rail__new"
              type="button"
              onClick={() => setNewListOpen(current => !current)}
              aria-expanded={newListOpen}
            >
              <Icon name={newListOpen ? 'minus' : 'plus'} size={16} /> Nouvelle liste
            </button>
          )}
          {newListOpen && (
            <form
              className="owner-new-list"
              onSubmit={async event => {
                event.preventDefault();
                setCreatingList(true);
                setNewListError('');
                try {
                  await onCreateList({
                    slug: newListSlug,
                    title: newListTitle,
                    subtitle: newListSubtitle,
                    occasion: newListOccasion,
                  });
                  setNewListOpen(false);
                  setNewListTitle('');
                  setNewListSlug('');
                  setNewListSlugEdited(false);
                  setNewListSubtitle('');
                  setNewListOccasion('');
                  setNewListTitleEdited(false);
                  setNewListSubtitleEdited(false);
                } catch (error) {
                  setNewListError(
                    error instanceof Error
                      ? error.message
                      : 'La liste n’a pas pu être créée.'
                  );
                } finally {
                  setCreatingList(false);
                }
              }}
            >
              <label htmlFor="new-list-title">
                Nom de la liste
                <input
                  id="new-list-title"
                  value={newListTitle}
                  onChange={event => {
                    const value = event.target.value;
                    setNewListTitle(value);
                    setNewListTitleEdited(true);
                    if (!newListSlugEdited) {
                      setNewListSlug(
                        value
                          .toLowerCase()
                          .normalize('NFD')
                          .replace(/[\u0300-\u036f]/g, '')
                          .replace(/[^a-z0-9]+/g, '-')
                          .replace(/^-|-$/g, '')
                          .slice(0, 48)
                      );
                    }
                  }}
                  required
                  minLength={2}
                />
              </label>
              <label htmlFor="new-list-occasion">
                Occasion
                <select
                  id="new-list-occasion"
                  value={newListOccasion}
                  onChange={event => {
                    const value = event.target.value;
                    const preset = occasionPresets.find(item => item.value === value);
                    setNewListOccasion(value);
                    if (preset && !newListTitleEdited) {
                      setNewListTitle(preset.title);
                      if (!newListSlugEdited) {
                        setNewListSlug(
                          preset.title
                            .toLowerCase()
                            .normalize('NFD')
                            .replace(/[\u0300-\u036f]/g, '')
                            .replace(/[^a-z0-9]+/g, '-')
                            .replace(/^-|-$/g, '')
                            .slice(0, 48)
                        );
                      }
                    }
                    if (preset && !newListSubtitleEdited) setNewListSubtitle(preset.subtitle);
                  }}
                >
                  <option value="">Aucune occasion particulière</option>
                  {occasionPresets.map(preset => (
                    <option key={preset.value} value={preset.value}>{preset.label}</option>
                  ))}
                </select>
                <small className="field-help">
                  Nous proposons un texte de départ, que vous pouvez modifier.
                </small>
              </label>
              <label htmlFor="new-list-slug">
                Adresse publique
                <span className="owner-new-list__slug">
                  souha.it/
                  <input
                    id="new-list-slug"
                    value={newListSlug}
                    onChange={event => {
                      setNewListSlugEdited(true);
                      setNewListSlug(event.target.value.toLowerCase())
                    }}
                    pattern="[a-z0-9](?:[a-z0-9\-]{0,46}[a-z0-9])?"
                    required
                  />
                </span>
              </label>
              <label htmlFor="new-list-subtitle">
                Petite introduction
                <input
                  id="new-list-subtitle"
                  value={newListSubtitle}
                  onChange={event => {
                    setNewListSubtitleEdited(true);
                    setNewListSubtitle(event.target.value);
                  }}
                  maxLength={180}
                />
              </label>
              {newListError && (
                <p className="form-error" role="alert">
                  {newListError}
                </p>
              )}
              <button type="submit" disabled={creatingList}>
                {creatingList ? 'Création…' : 'Créer la liste'}
              </button>
            </form>
          )}
        </aside>
        <div className="owner-list">
          {activeList ? (
            <>
              <div className="owner-list__top">
                <div>
                  <h2>{activeList.title}</h2>
                  <p className="owner-list__count">
                    {wishes.length} souhait{wishes.length > 1 ? 's' : ''}
                  </p>
                </div>
                <button type="button" onClick={addWish}>
                  {atWishLimit ? 'Débloquer plus de souhaits' : 'Ajouter un souhait'}
                  <Icon name={atWishLimit ? 'arrow-right' : 'plus'} size={17} />
                </button>
              </div>
              <div className="owner-share-list">
                <p>
                  {wishes.length ? 'Votre liste est prête à partager.' : 'Ajoutez un premier souhait, puis partagez le lien.'}
                  {' '}Les invités réservent gratuitement ; leurs noms restent privés.
                </p>
                <button type="button" disabled={!wishes.length} onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(`${window.location.origin}/${encodeURIComponent(activeList.slug)}`);
                    trackEvent('list_shared', 'owner', {channel: 'copy'});
                    setSavedMessage('Lien copié. Partagez-le dans la conversation de votre choix.');
                  } catch {
                    setSavedMessage('La copie est indisponible. Ouvrez la liste et copiez son adresse.');
                  }
                }}><Icon name="link" size={16} />Copier le lien de la liste</button>
              </div>
              {wishes.map(wish => (
            <article className="owner-wish-row" key={wish.id}>
              <span className="owner-wish-row__category">
                {categoryForWish(wish)}
              </span>
              <img
                src={directionSixImage(wish.image)}
                alt={wish.title}
                width={160}
                height={120}
                loading="lazy"
                onError={event => {
                  event.currentTarget.src =
                    '/images/souhait/illustrations/wish-fallback.webp';
                }}
              />
              <div>
                <h3>{wish.title}</h3>
                <p>{wish.merchant} · {wish.isReserved ? 'Réservé' : wish.status || modeLabel(wish.mode)}</p>
              </div>
              <strong>{formatCurrency(wish.price)}</strong>
              <button
                type="button"
                aria-label={`Modifier ${wish.title}`}
                onClick={() => { setSavedMessage(''); setEditingWish(wish); }}
              >
                <Icon name="pen" size={18} />
              </button>
            </article>
              ))}
              <section className="owner-payment" aria-labelledby="payment-title">
                <div>
                  <h3 id="payment-title">Relier le groupe au paiement</h3>
                  <p>
                    souha.it ne détient pas l’argent. Ajoutez le lien HTTPS de
                    votre prestataire pour que les contributeurs puissent
                    continuer après leur promesse.
                  </p>
                </div>
                {activeList.canCollect ? (
                  <form
                    onSubmit={async event => {
                      event.preventDefault();
                      setSavingPayment(true);
                      setPaymentError('');
                      try {
                        await onUpdatePayment(
                          activeList.slug,
                          paymentUrl,
                          paymentLabel
                        );
                        setSavedMessage('Lien de paiement mis à jour.');
                      } catch (updateError) {
                        setPaymentError(
                          updateError instanceof Error
                            ? updateError.message
                            : 'Le lien ne peut pas être enregistré.'
                        );
                      } finally {
                        setSavingPayment(false);
                      }
                    }}
                  >
                    <label htmlFor="owner-payment-url">
                      Lien de paiement externe
                      <input
                        id="owner-payment-url"
                        type="url"
                        inputMode="url"
                        placeholder="https://paypal.me/..."
                        value={paymentUrl}
                        onChange={event => setPaymentUrl(event.target.value)}
                      />
                    </label>
                    <label htmlFor="owner-payment-label">
                      Libellé du bouton
                      <input
                        id="owner-payment-label"
                        maxLength={80}
                        placeholder="Continuer vers le paiement"
                        value={paymentLabel}
                        onChange={event => setPaymentLabel(event.target.value)}
                      />
                    </label>
                    {paymentError && (
                      <p className="form-error" role="alert">{paymentError}</p>
                    )}
                    <button type="submit" disabled={savingPayment}>
                      {savingPayment ? 'Enregistrement…' : 'Enregistrer le lien'}
                    </button>
                  </form>
                ) : (
                  <div className="owner-payment__locked">
                    <Icon name="lock" size={23} />
                    <p>
                      Le relais de paiement est inclus dans le Pass occasion et
                      Plus. Les promesses sans paiement restent gratuites.
                    </p>
                    <button type="button" onClick={() => openOffers('payment_link')}>
                      Comparer les offres
                    </button>
                  </div>
                )}
              </section>
            </>
          ) : (
            <div className="owner-first-list">
              <Motif name="gift" size={78} />
              <h2>Créez votre première liste.</h2>
              <p>
                Donnez-lui un nom et une adresse. Vous pourrez ajouter le
                premier souhait juste après.
              </p>
              <button type="button" onClick={() => setNewListOpen(true)}>
                Commencer gratuitement
              </button>
            </div>
          )}
        </div>
      </section>

      {activeList && <section className="gift-room" id="gift-room">
        <div>
          <h2>Vos cadeaux en cours</h2>
          <p>
            Vos proches s’organisent sans vous révéler leurs noms, leurs
            montants ni leurs messages.
          </p>
        </div>
        <div className="gift-room__summary">
          <span>Cadeaux collectifs commencés</span>
          <strong>{wishes.filter(wish => wish.mode !== 'gift' && wish.pledged > 0).length}</strong>
          <small>Avancement global de votre liste</small>
        </div>
        <div className="gift-room__privacy">
          <Icon name="lock" size={24} />
          <p>
            Les noms, les montants individuels et les messages restent privés
            pendant l’organisation du cadeau.
          </p>
        </div>
      </section>}

      {addOpen && activeList && (
        <CaptureDrawer
          onClose={() => setAddOpen(false)}
          onSave={async wish => {
            await onAddWish(wish);
            setAddOpen(false);
            setSavedMessage('Souhait ajouté à votre liste publique.');
          }}
          count={wishes.length}
        />
      )}
      {editingWish && (
        <CaptureDrawer
          initialWish={editingWish}
          onClose={() => setEditingWish(null)}
          onSave={async wish => {
            await onUpdateWish(wish);
            setEditingWish(null);
            setSavedMessage('Souhait mis à jour sur votre liste publique.');
          }}
          onArchive={async () => {
            await onArchiveWish(editingWish);
            setEditingWish(null);
            setSavedMessage('Souhait archivé.');
          }}
          count={wishes.length}
        />
      )}
      {savedMessage && !addOpen && !editingWish && (
        <Toast message={savedMessage} onDismiss={() => setSavedMessage('')} />
      )}
    </main>
  );
};

const CaptureDrawer: FC<{
  onClose: () => void;
  onSave: (wish: Wish) => Promise<void>;
  onArchive?: () => Promise<void>;
  count: number;
  initialWish?: Wish;
}> = ({onClose, onSave, onArchive, count, initialWish}) => {
  useBodyLock();
  const drawer = useRef<HTMLElement | null>(null);
  const closeButton = useRef<HTMLButtonElement | null>(null);
  const opener = useRef<HTMLElement | null>(document.activeElement as HTMLElement);
  const closeAction = useRef(onClose);
  useEffect(() => { closeAction.current = onClose; }, [onClose]);
  useEffect(() => {
    const openingControl = opener.current;
    closeButton.current && closeButton.current.focus();
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeAction.current();
      }
      if (event.key === 'Tab' && drawer.current) {
        const focusable = Array.from(drawer.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )).filter(element => element.offsetParent !== null);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('keydown', handleKey);
      window.setTimeout(() => {
        if (openingControl && openingControl.isConnected) openingControl.focus();
      }, 0);
    };
  }, []);
  const [step, setStep] = useState<CaptureStep>(
    initialWish ? 'review' : 'url'
  );
  const [url, setUrl] = useState(initialWish ? initialWish.sourceUrl : '');
  const [draft, setDraft] = useState<MetadataDraft | null>(() =>
    initialWish
      ? {
          url: initialWish.sourceUrl,
          title: initialWish.title,
          merchant: initialWish.merchant,
          image: initialWish.image,
          price: String(initialWish.price),
          edition: initialWish.edition || '',
          note: initialWish.note,
          mode: initialWish.mode,
          deadline: initialWish.deadline || '',
        }
      : null
  );
  const [extractionNote, setExtractionNote] = useState('');
  const [saveError, setSaveError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const findDetails = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStep('loading');
    const result = await extractMetadata(url);
    setDraft(result);
    setExtractionNote(
      result.provider === 'amazon' && result.previewStatus === 'partial'
        ? 'Amazon n’a pas transmis les détails. Le lien propre est prêt ; complétez les champs avant d’enregistrer.'
        : result.previewStatus === 'complete'
        ? 'Détails trouvés sur la page. Tout reste modifiable.'
        : 'Le lien est prêt. Complétez les détails avant d’enregistrer.'
    );
    setStep('review');
  };

  const update = (field: keyof MetadataDraft, value: string) => {
    setDraft(current => (current ? {...current, [field]: value} : current));
  };

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!draft) return;
    const wish: Wish = {
      id: initialWish ? initialWish.id : `wish-${Date.now()}`,
      number: initialWish
        ? initialWish.number
        : String(count + 1).padStart(2, '0'),
      title: draft.title,
      note: draft.note,
      image: draft.image || '/images/souhait/hero-collection.jpg',
      merchant: draft.merchant,
      price: Number(draft.price) || 0,
      pledged: initialWish ? initialWish.pledged : 0,
      pledgers: initialWish ? initialWish.pledgers : 0,
      mode: draft.mode,
      shape: initialWish
        ? initialWish.shape
        : count % 2 === 0
        ? 'wish--wide'
        : 'wish--portrait',
      sourceUrl: draft.url,
      edition: draft.edition || undefined,
      deadline: draft.deadline || undefined,
      status: initialWish ? initialWish.status : 'New',
      custom: initialWish ? initialWish.custom : true,
      myContributionId: initialWish && initialWish.myContributionId,
      myReservationId: initialWish && initialWish.myReservationId,
      isReserved: initialWish && initialWish.isReserved,
    };
    setIsSaving(true);
    setSaveError('');
    try {
      await onSave(wish);
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : 'Le souhait n’a pas pu être enregistré.'
      );
      setIsSaving(false);
    }
  };

  return (
    <div className="drawer-shell" role="presentation">
      <button
        className="drawer-shell__backdrop"
        type="button"
        aria-label="Fermer le panneau d’ajout"
        onClick={onClose}
      />
      <aside
        className="wish-drawer capture-drawer"
        role="dialog"
        aria-modal="true"
        ref={drawer}
        aria-label={
          initialWish ? `Modifier ${initialWish.title}` : 'Ajouter un souhait'
        }
      >
        <div className="drawer__top">
          <p className="drawer__context">
            <Icon name={initialWish ? 'pen' : 'gift'} size={17} />
            {initialWish
              ? 'Modifier ce souhait'
              : step === 'review'
              ? 'À vous de jouer'
              : 'Nouveau souhait'}
          </p>
          <button
            className="close-button"
            type="button"
            aria-label="Fermer"
            onClick={onClose}
            ref={closeButton}
          >
            <Icon name="close" size={20} />
          </button>
        </div>

        {step === 'url' && (
          <>
            <h2>Qu’avez-vous repéré ?</h2>
            <p className="drawer__intro">
              Collez le lien d’un objet, d’une expérience ou d’une boutique.
            </p>
            <form onSubmit={findDetails}>
              <label htmlFor="wish-url">Lien du souhait</label>
              <div className="url-input">
                <Icon name="link" size={19} />
                <input
                  id="wish-url"
                  type="url"
                  value={url}
                  onChange={event => setUrl(event.target.value)}
                  placeholder="https://boutique.fr/mon-souhait"
                  required
                  autoFocus
                />
              </div>
              <p className="capture-hint">
                Les liens courts sont acceptés. Vous vérifierez tout avant
                l’ajout.
              </p>
              <button className="primary-button drawer__submit" type="submit">
                Récupérer les détails <Icon name="arrow-right" size={18} />
              </button>
            </form>
          </>
        )}

        {step === 'loading' && (
          <div className="capture-loading" role="status">
            <span />
            <h2>Lecture de<br />la page…</h2>
            <p>Nous cherchons le nom, l’image, la boutique et le prix.</p>
          </div>
        )}

        {step === 'review' && draft && (
          <>
            <div className="capture-preview">
              <img
                src={directionSixImage(draft.image)}
                alt={draft.title}
                width={640}
                height={480}
                onError={event => {
                  event.currentTarget.src =
                    '/images/souhait/illustrations/wish-fallback.webp';
                }}
              />
              <span>{extractionNote}</span>
            </div>
            <form onSubmit={save}>
              <div className="field-pair">
                <label>
                  Nom
                  <input
                    value={draft.title}
                    onChange={event => update('title', event.target.value)}
                    required
                  />
                </label>
                <label>
                  Prix en euros
                  <input
                    type="number"
                    min="0"
                    value={draft.price}
                    onChange={event => update('price', event.target.value)}
                    required
                  />
                </label>
              </div>
              <div className="field-pair">
                <label>
                  Marque ou boutique
                  <input
                    value={draft.merchant}
                    onChange={event => update('merchant', event.target.value)}
                    required
                  />
                </label>
                <label>
                  Édition ou taille
                  <input
                    value={draft.edition}
                    onChange={event => update('edition', event.target.value)}
                    placeholder="Facultatif"
                  />
                </label>
              </div>
              <label>
                Adresse de l’image
                <input
                  type="text"
                  value={draft.image}
                  onChange={event => update('image', event.target.value)}
                  required
                />
              </label>
              <label>
                Pourquoi celui-ci ?
                <textarea
                  value={draft.note}
                  onChange={event => update('note', event.target.value)}
                  placeholder="L’édition signée, si elle est encore disponible…"
                  rows={3}
                  required
                />
              </label>
              <label>
                S’organiser avant le
                <input
                  type="date"
                  value={draft.deadline}
                  onChange={event => update('deadline', event.target.value)}
                />
                <small className="field-help">
                  Facultatif. Cette date ne déclenche aucun paiement.
                </small>
              </label>
              <fieldset>
                <legend>Comment vos proches peuvent-ils aider ?</legend>
                {captureModes.map(option => (
                  <label className="mode-option" key={option.mode}>
                    <input
                      type="radio"
                      name="mode"
                      checked={draft.mode === option.mode}
                      onChange={() =>
                        setDraft(current =>
                          current ? {...current, mode: option.mode} : current
                        )
                      }
                    />
                    <span>
                      <b>{option.label}</b>
                      <small>{option.description}</small>
                    </span>
                  </label>
                ))}
              </fieldset>
              {saveError && (
                <p className="form-error" role="alert">
                  {saveError}
                </p>
              )}
              <button
                className="primary-button drawer__submit"
                type="submit"
                disabled={isSaving}
              >
                {isSaving ? 'Enregistrement…' : initialWish ? 'Enregistrer les modifications' : 'Ajouter ce souhait'}{' '}
                <Icon name="arrow-right" size={18} />
              </button>
              {initialWish && onArchive && (
                <button
                  className="capture-archive"
                  type="button"
                  disabled={isSaving}
                  onClick={async () => {
                    if (
                      !window.confirm(
                        `Archiver « ${initialWish.title} » ? L’activité existante restera dans votre historique privé.`
                      )
                    ) {
                      return;
                    }
                    setIsSaving(true);
                    setSaveError('');
                    try {
                      await onArchive();
                    } catch (error) {
                      setSaveError(
                        error instanceof Error
                          ? error.message
                          : 'Le souhait n’a pas pu être archivé.'
                      );
                      setIsSaving(false);
                    }
                  }}
                >
                  Archiver ce souhait
                </button>
              )}
            </form>
          </>
        )}
      </aside>
    </div>
  );
};

const WishDetail: FC<{
  wish: Wish;
  pledges: Pledge[];
  captains: string[];
  onPrevious: () => void;
  onNext: () => void;
  onClose: () => void;
  onPledge: (pledge: Pledge) => Promise<string | void>;
  onReserve: (
    name: string,
    email: string,
    privateMessage: string,
    turnstileToken: string
  ) => Promise<string | void>;
  onCaptain: (
    name: string,
    email: string,
    turnstileToken: string
  ) => Promise<string | void>;
  onRevealMessages: () => Promise<void>;
  ownerLabel: string;
  paymentUrl: string;
  paymentLabel: string;
}> = ({
  wish,
  pledges,
  captains,
  onPrevious,
  onNext,
  onClose,
  onPledge,
  onReserve,
  onCaptain,
  onRevealMessages,
  ownerLabel,
  paymentUrl,
  paymentLabel,
}) => {
  useBodyLock();
  const touchStartX = useRef<number | null>(null);
  const dialog = useRef<HTMLDivElement | null>(null);
  const closeButton = useRef<HTMLButtonElement | null>(null);
  const initialAmount = Math.min(25, Math.max(1, wish.price - wish.pledged));
  const [amount, setAmount] = useState(initialAmount);
  const [customSelected, setCustomSelected] = useState(![15, 25, 50].includes(initialAmount));
  const [customAmount, setCustomAmount] = useState(String(initialAmount));
  const [captainOpen, setCaptainOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileAttempt, setTurnstileAttempt] = useState(0);
  const [message, setMessage] = useState('');
  const [recipientMessage, setRecipientMessage] = useState('');
  const [savedMessage, setSavedMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [coordinationOptIn, setCoordinationOptIn] = useState(false);
  const [formStep, setFormStep] = useState<1 | 2>(1);
  const wishPledges = pledges.filter(pledge => pledge.wishId === wish.id);
  const pledged =
    wish.pledged + wishPledges.reduce((total, pledge) => total + pledge.amount, 0);
  const amCaptain = captains.includes(wish.id);
  const hasCaptain = amCaptain || wish.hasCaptain;
  const readyForReveal = wish.status === 'Acheté' || pledged >= wish.price;
  const pledgeAmount = customSelected ? Number(customAmount) : amount;
  const remainingAmount = Math.max(0, wish.price - pledged);
  const hasValidPledgeAmount =
    Number.isFinite(pledgeAmount) &&
    pledgeAmount >= 1 &&
    pledgeAmount <= remainingAmount;
  useEffect(() => {
    setFormStep(1);
    setTurnstileToken('');
    setTurnstileAttempt(current => current + 1);
    setSubmitError('');
    setAcceptTerms(false);
    setCoordinationOptIn(false);
    setMessage('');
    setRecipientMessage('');
  }, [wish.id]);
  useEffect(() => {
    closeButton.current && closeButton.current.focus();
    const handleKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      const isEditing = Boolean(
        target.closest('input, textarea, select, [contenteditable="true"]')
      );
      if (!isEditing && event.key === 'ArrowLeft') onPrevious();
      if (!isEditing && event.key === 'ArrowRight') onNext();
      if (event.key === 'Escape') onClose();
      if (event.key === 'Tab' && dialog.current) {
        const focusable = Array.from(
          dialog.current.querySelectorAll<HTMLElement>(
            'button:not([disabled]), a[href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        ).filter(element => element.offsetParent !== null && element.tabIndex >= 0);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, onNext, onPrevious]);

  const finishSwipe = (clientX: number) => {
    if (touchStartX.current === null) return;
    const distance = clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(distance) < 48) return;
    distance < 0 ? onNext() : onPrevious();
  };
  const pledge = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const recoveryCode = await onPledge({
        id: `pledge-${Date.now()}`,
        wishId: wish.id,
        amount: pledgeAmount,
        name,
        email,
        turnstileToken,
        message,
        recipientMessage,
        coordinationOptIn,
        createdAt: new Date().toISOString(),
      });
      setSavedMessage(
        `${formatCurrency(
          pledgeAmount
        )} promis. ${
          paymentUrl
            ? 'Vous pouvez maintenant utiliser le lien de paiement externe.'
            : 'Rien n’a été débité ; le capitaine organisera la suite.'
        }${
          recoveryCode
            ? ` Conservez votre code privé : ${recoveryCode}`
            : ''
        }`
      );
    } catch (error) {
      setTurnstileToken('');
      setTurnstileAttempt(current => current + 1);
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'La promesse n’a pas pu être enregistrée.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="detail-shell"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`wish-detail-title-${wish.id}`}
      ref={dialog}
    >
      <button
        className="detail-shell__backdrop"
        type="button"
        tabIndex={-1}
        aria-label="Fermer le souhait"
        onClick={onClose}
      />
      <article
        className="wish-detail"
        onPointerDown={event => {
          const target = event.target as HTMLElement;
          if (
            event.pointerType !== 'touch' ||
            target.closest('button, a, input, textarea')
          ) {
            return;
          }
          touchStartX.current = event.clientX;
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerUp={event => finishSwipe(event.clientX)}
        onPointerCancel={() => {
          touchStartX.current = null;
        }}
      >
        <button
          className="close-button wish-detail__close"
          type="button"
          aria-label="Fermer"
          onClick={onClose}
          ref={closeButton}
        >
          <Icon name="close" size={20} />
        </button>
        <nav className="wish-detail__pager" aria-label="Parcourir les souhaits">
          <button type="button" aria-label="Souhait précédent" onClick={onPrevious}><Icon name="arrow-left" size={20} /></button>
          <span>Parcourir</span>
          <button type="button" aria-label="Souhait suivant" onClick={onNext}><Icon name="arrow-right" size={20} /></button>
        </nav>
        <div className="wish-detail__visual">
          <img
            src={directionSixImage(wish.image)}
            alt={wish.title}
            width={900}
            height={900}
            onError={event => {
              event.currentTarget.src =
                '/images/souhait/illustrations/wish-fallback.webp';
            }}
          />
        </div>
        <div className="wish-detail__content">
          <p className="wish-detail__context">
            <Icon name="gift" size={16} />
            {wish.merchant} · {wish.status || 'Disponible'}
          </p>
          <h2 id={`wish-detail-title-${wish.id}`}>{wish.title}</h2>
          <blockquote>“{wish.note}”</blockquote>
          {wish.edition && <p className="edition-note">{wish.edition}</p>}
          <div className="detail-price">
            <span>{formatCurrency(wish.price)}</span>
            <small>
              {wish.deadline
                ? `À organiser avant le ${formatDeadline(wish.deadline)}`
                : 'Aucune date limite'}
            </small>
          </div>

          {wish.mode !== 'gift' ? (
            <div className="contribution">
              <div className="pledge-notice">
                <b>souha.it ne détient pas l’argent.</b>
                <span>
                  {paymentUrl
                    ? 'Enregistrez votre promesse, puis utilisez le service de paiement externe.'
                    : 'Vous promettez de participer : rien n’est débité ici.'}
                </span>
              </div>
              <div className="contribution__summary">
                <p>
                  <strong>{formatCurrency(Math.min(wish.price, pledged))}</strong>{' '}
                  promis
                </p>
                <p>
                  {Math.max(0, wish.price - pledged) === 0
                    ? 'Le groupe est prêt'
                    : `Encore ${formatCurrency(wish.price - pledged)}`}
                </p>
              </div>
              <div className="progress">
                <span
                  style={{
                    width: `${Math.min(100, Math.round((pledged / wish.price) * 100))}%`,
                  }}
                />
              </div>
              <p className="contributors">
                {wish.pledgers + wishPledges.length}{' '}
                {wish.pledgers + wishPledges.length === 1
                  ? 'personne a'
                  : 'personnes ont'}{' '}
                fait une promesse.
              </p>
              {wish.deadline && (
                <p className="deadline-note">
                  Le groupe souhaite s’organiser avant le{' '}
                  <strong>{formatDeadline(wish.deadline)}</strong>.
                </p>
              )}
              {wish.messageRevealed && wish.giftMessages && wish.giftMessages.length > 0 && (
                <section className="gift-messages" aria-label="Messages du groupe">
                  <h3>Le mot du groupe</h3>
                  {wish.giftMessages.map((giftMessage, index) => (
                    <blockquote key={`${wish.id}-message-${index}`}>“{giftMessage}”</blockquote>
                  ))}
                </section>
              )}
              {!savedMessage && !captainOpen && !wish.myContributionId && remainingAmount > 0 && (
                <form className="pledge-form" onSubmit={pledge}>
                  {formStep === 1 ? (
                    <div className="pledge-step pledge-step--amount">
                      <p className="pledge-step__label">Votre participation</p>
                      <div className="amounts">
                        {[15, 25, 50].map(value => (
                          <button
                            className={!customSelected && amount === value ? 'is-active' : ''}
                            type="button"
                            key={value}
                            aria-pressed={!customSelected && amount === value}
                            disabled={value > remainingAmount}
                            onClick={() => {
                              setAmount(value);
                              setCustomSelected(false);
                            }}
                          >
                            {formatCurrency(value)}
                          </button>
                        ))}
                        <button
                          className={customSelected ? 'is-active' : ''}
                          type="button"
                          aria-pressed={customSelected}
                          onClick={() => setCustomSelected(true)}
                        >
                          Autre
                        </button>
                      </div>
                      {customSelected && (
                        <label className="custom-amount">
                          <span aria-hidden="true">€</span>
                          <input
                            aria-label="Montant personnalisé"
                            type="number"
                            min="1"
                            max={remainingAmount}
                            step="1"
                            inputMode="numeric"
                            placeholder="Votre montant"
                            value={customAmount}
                            onChange={event => setCustomAmount(event.target.value)}
                            required
                            autoFocus
                          />
                        </label>
                      )}
                      <button
                        className="primary-button contribution__button"
                        type="button"
                        onClick={() => {
                          setSubmitError('');
                          setFormStep(2);
                        }}
                        disabled={!hasValidPledgeAmount}
                      >
                        {pledgeAmount > remainingAmount
                          ? `Maximum : ${formatCurrency(remainingAmount)}`
                          : hasValidPledgeAmount
                          ? `Continuer avec ${formatCurrency(pledgeAmount)}`
                          : 'Saisissez un montant'}
                      </button>
                    </div>
                  ) : (
                    <div className="pledge-step pledge-step--details">
                      <div className="pledge-step__summary">
                        <span>Votre promesse</span>
                        <strong>{formatCurrency(pledgeAmount)}</strong>
                        <button
                          type="button"
                          onClick={() => {
                            setFormStep(1);
                            setTurnstileToken('');
                          }}
                        >
                          Modifier
                        </button>
                      </div>
                      <div className="pledge-fields">
                        <label>Votre prénom<input
                          aria-label="Votre prénom"
                          autoComplete="given-name"
                          minLength={2}
                          placeholder="Sam"
                          value={name}
                          onChange={event => setName(event.target.value)}
                          required
                          autoFocus
                        /></label>
                        <label>E-mail (facultatif)<input
                          className="pledge-fields__email"
                          aria-label="Votre adresse e-mail"
                          type="email"
                          autoComplete="email"
                          placeholder="vous@exemple.fr"
                          value={email}
                          onChange={event => setEmail(event.target.value)}
                        /></label>
                        <label>Note privée (facultatif)<input
                          aria-label="Note privée"
                          placeholder="Une précision pour le groupe"
                          value={message}
                          onChange={event => setMessage(event.target.value)}
                        /></label>
                        <label>Mot pour le destinataire (facultatif)<input
                          aria-label="Message pour le destinataire"
                          placeholder="Votre petit mot"
                          value={recipientMessage}
                          onChange={event => setRecipientMessage(event.target.value)}
                        /></label>
                      </div>
                      <p className="privacy-note">
                        Aucun compte requis. Le propriétaire de la liste ne voit
                        ni votre nom ni votre montant, seulement le total du groupe.
                      </p>
                      <label className="pledge-terms">
                        <input
                          type="checkbox"
                          checked={acceptTerms}
                          onChange={event => setAcceptTerms(event.target.checked)}
                          required
                        />
                        <span>
                          Je comprends qu’il s’agit d’une promesse privée, à
                          confirmer ou annuler depuis ma page d’activité.
                        </span>
                      </label>
                      <label className="pledge-terms pledge-terms--optional">
                        <input
                          type="checkbox"
                          checked={coordinationOptIn}
                          onChange={event =>
                            setCoordinationOptIn(event.target.checked)
                          }
                        />
                        <span>
                          M’envoyer par e-mail les nouvelles privées du capitaine.
                          Mon adresse reste cachée au capitaine et au propriétaire.
                        </span>
                      </label>
                      <TurnstileWidget
                        key={turnstileAttempt}
                        onToken={setTurnstileToken}
                      />
                      {submitError && (
                        <p className="form-error" role="alert">
                          {submitError}
                        </p>
                      )}
                      <button
                        className="primary-button contribution__button"
                        type="submit"
                        disabled={
                          isSubmitting ||
                          !acceptTerms ||
                          name.trim().length < 2 ||
                          (coordinationOptIn && !email.includes('@')) ||
                          !turnstileToken ||
                          !hasValidPledgeAmount
                        }
                      >
                        {isSubmitting
                          ? 'Enregistrement…'
                          : `Promettre ${formatCurrency(pledgeAmount)}`}
                      </button>
                    </div>
                  )}
                </form>
              )}
              {!savedMessage && wish.myContributionId && !captainOpen && (
                <div className="commitment-note"><Icon name="check" size={18} /><p>Votre participation est enregistrée.<a href="/activity">Gérer ma participation <Icon name="arrow-right" size={16} /></a></p></div>
              )}
            </div>
          ) : (
            <div className="reservation">
              <p>
                Réservez ce cadeau en privé. {ownerLabel} verra seulement qu’il
                est pris en charge, jamais qui l’a réservé.
              </p>
              <p className="privacy-note">
                Aucun compte requis. Conservez le code affiché après la
                réservation pour la modifier plus tard.
              </p>
              {!savedMessage && !wish.isReserved && (
                <>
                  {formStep === 1 ? (
                    <button
                      className="primary-button contribution__button reservation__start"
                      type="button"
                      onClick={() => setFormStep(2)}
                    >
                      Réserver ce cadeau
                    </button>
                  ) : (
                    <div className="pledge-step pledge-step--details">
                      <div className="pledge-step__summary">
                        <span>Réservation privée</span>
                        <strong>{formatCurrency(wish.price)}</strong>
                        <button
                          type="button"
                          onClick={() => {
                            setFormStep(1);
                            setTurnstileToken('');
                          }}
                        >
                          Retour
                        </button>
                      </div>
                      <div className="pledge-fields">
                        <label>Votre prénom<input
                          aria-label="Votre prénom"
                          autoComplete="given-name"
                          minLength={2}
                          placeholder="Sam"
                          value={name}
                          onChange={event => setName(event.target.value)}
                          required
                          autoFocus
                        /></label>
                        <label>E-mail (facultatif)<input
                          className="pledge-fields__email"
                          aria-label="Votre adresse e-mail"
                          type="email"
                          autoComplete="email"
                          placeholder="vous@exemple.fr"
                          value={email}
                          onChange={event => setEmail(event.target.value)}
                        /></label>
                        <label>Note privée (facultatif)<input
                          aria-label="Note privée"
                          placeholder="Une précision pour le groupe"
                          value={message}
                          onChange={event => setMessage(event.target.value)}
                        /></label>
                      </div>
                      <label className="pledge-terms">
                        <input
                          type="checkbox"
                          checked={acceptTerms}
                          onChange={event => setAcceptTerms(event.target.checked)}
                        />
                        <span>
                          J’accepte de confirmer ou libérer cette réservation plus tard.
                        </span>
                      </label>
                      <TurnstileWidget
                        key={turnstileAttempt}
                        onToken={setTurnstileToken}
                      />
                      {submitError && (
                        <p className="form-error" role="alert">
                          {submitError}
                        </p>
                      )}
                      <button
                        className="primary-button contribution__button"
                        type="button"
                        disabled={
                          isSubmitting ||
                          name.trim().length < 2 ||
                          (email !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) ||
                          !turnstileToken ||
                          !acceptTerms
                        }
                        onClick={async () => {
                      setIsSubmitting(true);
                      setSubmitError('');
                      try {
                        const recoveryCode = await onReserve(
                          name,
                          email,
                          message,
                          turnstileToken
                        );
                        setSavedMessage(
                          `C’est à vous de l’offrir. Les autres verront seulement qu’il est pris en charge.${
                            recoveryCode
                              ? ` Conservez votre code privé : ${recoveryCode}`
                              : ''
                          }`
                        );
                      } catch (error) {
                        setTurnstileToken('');
                        setTurnstileAttempt(current => current + 1);
                        setSubmitError(
                          error instanceof Error
                            ? error.message
                            : 'La réservation n’a pas pu être enregistrée.'
                        );
                      } finally {
                        setIsSubmitting(false);
                      }
                    }}
                      >
                        {isSubmitting ? 'Réservation…' : 'Je m’en occupe'}
                      </button>
                    </div>
                  )}
                </>
              )}
              {!savedMessage && wish.isReserved && <div className="commitment-note"><Icon name="gift" size={18} /><p>{wish.myReservationId ? 'Vous avez réservé ce cadeau.' : 'Ce cadeau est déjà pris en charge.'}{wish.myReservationId && <a href="/activity">Gérer ma réservation <Icon name="arrow-right" size={16} /></a>}</p></div>}
            </div>
          )}
          {savedMessage && (
            <div className="success-message" role="status">
              <p><Icon name="check" size={18} /> {savedMessage}</p>
              {wish.mode !== 'gift' && safeExternalUrl(paymentUrl) && (
                <a
                  href={safeExternalUrl(paymentUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    const provider = paymentProviderCategory(paymentUrl);
                    trackEvent('payment_handoff_opened', 'public', {provider});
                  }}
                >
                  {paymentLabel || 'Continuer vers le paiement'} <Icon name="external" size={16} />
                </a>
              )}
              <a className="success-message__own-list" href="/me">
                Créer ma propre liste
              </a>
              <a href="/activity">Voir mon activité <Icon name="arrow-right" size={16} /></a>
            </div>
          )}
          {wish.mode !== 'gift' && (
            <div className="captain-callout">
              <div>
                <b>
                  {hasCaptain
                    ? 'Un capitaine coordonne le cadeau.'
                    : 'Voulez-vous coordonner le cadeau ?'}
                </b>
                <p>
                  Le capitaine vérifie la disponibilité, confirme le groupe et
                  organise l’achat final en dehors de souha.it.
                </p>
              </div>
              {!hasCaptain && !captainOpen && (
                <button type="button" onClick={() => {
                  setSubmitError('');
                  setTurnstileToken('');
                  setCaptainOpen(true);
                }}>
                  Je coordonne <Icon name="arrow-right" size={16} />
                </button>
              )}
              {captainOpen && !hasCaptain && (
                <form className="captain-enrollment" onSubmit={async event => {
                  event.preventDefault();
                  setIsSubmitting(true);
                  setSubmitError('');
                  try {
                    const recoveryCode = await onCaptain(name, email, turnstileToken);
                    setCaptainOpen(false);
                    setSavedMessage(`Vous êtes capitaine du cadeau. Retrouvez son suivi dans votre activité privée.${recoveryCode ? ` Conservez votre code privé : ${recoveryCode}` : ''}`);
                  } catch (error) {
                    setTurnstileToken('');
                    setTurnstileAttempt(current => current + 1);
                    setSubmitError(error instanceof Error ? error.message : 'Le rôle de capitaine n’a pas pu être enregistré.');
                  } finally {
                    setIsSubmitting(false);
                  }
                }}>
                  <div className="pledge-fields">
                    <label>Votre prénom
                      <input aria-label="Votre prénom" placeholder="Sam" autoComplete="given-name" minLength={2} required autoFocus value={name} onChange={event => setName(event.target.value)} />
                    </label>
                    <label>E-mail (facultatif)
                      <input aria-label="Votre adresse e-mail" type="email" placeholder="vous@exemple.fr" autoComplete="email" value={email} onChange={event => setEmail(event.target.value)} />
                    </label>
                  </div>
                  <p>Votre identité reste privée. Vous coordonnez la disponibilité et l’achat du cadeau en dehors de souha.it.</p>
                  <TurnstileWidget key={turnstileAttempt} onToken={setTurnstileToken} />
                  {submitError && <p className="form-error" role="alert">{submitError}</p>}
                  <div className="captain-enrollment__actions">
                    <button className="primary-button" type="submit" disabled={isSubmitting || name.trim().length < 2 || !turnstileToken}>
                      {isSubmitting ? 'Enregistrement…' : 'Devenir capitaine'} <Icon name="check" size={16} />
                    </button>
                    <button type="button" onClick={() => {setCaptainOpen(false);setSubmitError('');}}>Retour</button>
                  </div>
                </form>
              )}
              {amCaptain && <a href="/activity">Coordonner dans mon activité <Icon name="arrow-right" size={16} /></a>}
              {submitError && !captainOpen && (savedMessage || formStep === 1) && <p className="form-error" role="alert">{submitError}</p>}
              {amCaptain && readyForReveal && !wish.messageRevealed && (wish.giftMessageCount || 0) > 0 && (
                <button
                  type="button"
                  className="captain-reveal-button"
                  onClick={async () => {
                    try {
                      await onRevealMessages();
                    } catch (error) {
                      setSubmitError(
                        error instanceof Error
                          ? error.message
                          : 'Les messages du groupe n’ont pas pu être affichés.'
                      );
                    }
                  }}
                >
                  Afficher les messages du groupe <Icon name="arrow-right" size={16} />
                </button>
              )}
            </div>
          )}
          {safeExternalUrl(wish.sourceUrl) && (
            <a
              className="merchant-link"
              href={safeExternalUrl(wish.sourceUrl)}
              target="_blank"
              rel="noopener noreferrer"
            >
              Voir chez {wish.merchant} <Icon name="external" size={16} />
            </a>
          )}
        </div>
      </article>
    </div>
  );
};

const Toast: FC<{message: string; onDismiss: () => void}> = ({
  message,
  onDismiss,
}) => (
  <div className="toast" role="status">
    <Icon name="check" size={19} /><span>{message}</span>
    <button type="button" aria-label="Fermer" onClick={onDismiss}><Icon name="close" size={18} /></button>
  </div>
);

const Souhait: FC = () => {
  const [route, setRoute] = useState(window.location.pathname);
  const [wishes, setWishes] = useState<Wish[]>(() =>
    DEMO_MODE ? safeRead(WISHES_KEY, initialWishes) : []
  );
  const [pledges, setPledges] = useState<Pledge[]>(() =>
    safeRead(PLEDGES_KEY, [])
  );
  const [captains, setCaptains] = useState<string[]>(() =>
    safeRead(CAPTAINS_KEY, [])
  );
  const [wishlist, setWishlist] = useState<ApiWishlist | null>(null);
  const [ownerLists, setOwnerLists] = useState<ApiOwnerWishlist[]>([]);
  const [ownerAccount, setOwnerAccount] = useState<ApiOwnerAccount | null>(null);
  const [ownerSessionLoaded, setOwnerSessionLoaded] = useState(false);
  const [ownerSessionError, setOwnerSessionError] = useState(false);
  const [ownerSessionAttempt, setOwnerSessionAttempt] = useState(0);
  const [cloudMode, setCloudMode] = useState(false);
  const [serviceUnavailable, setServiceUnavailable] = useState(false);
  const [loadedSlug, setLoadedSlug] = useState('');
  const [collectionState, setCollectionState] = useState<'loading' | 'ready' | 'missing' | 'unavailable'>('loading');
  const refreshRequest = useRef(0);
  const lastAvailableSlug = useRef('');

  const routeInfo = useMemo(() => {
    let parts: string[];
    try {
      parts = route
        .split('/')
        .filter(Boolean)
        .map(part => decodeURIComponent(part));
    } catch {
      return {kind: 'marketing' as const, slug: null, wishId: undefined};
    }
    if (!parts.length) {
      return {kind: 'marketing' as const, slug: null, wishId: undefined};
    }
    if (parts[0] === 'activity') {
      return {kind: 'activity' as const, slug: null, wishId: undefined};
    }
    if (parts[0] === 'pricing') {
      return {kind: 'pricing' as const, slug: null, wishId: undefined};
    }
    if (parts[0] === 'privacy') {
      return {kind: 'privacy' as const, slug: null, wishId: undefined};
    }
    if (parts[0] === 'terms') {
      return {kind: 'terms' as const, slug: null, wishId: undefined};
    }
    if (parts[0] === 'me') {
      const slug =
        parts[1] && WISHLIST_SLUG.test(parts[1]) ? parts[1] : null;
      return {kind: 'owner' as const, slug, wishId: undefined};
    }
    const slug = parts[0] === 'lia-julien' ? 'julien' : parts[0];
    if (!WISHLIST_SLUG.test(slug)) {
      return {kind: 'marketing' as const, slug: null, wishId: undefined};
    }
    return {
      kind: 'public' as const,
      slug,
      wishId: parts[1],
    };
  }, [route]);
  const activeSlug = routeInfo.slug;

  const refreshWishlist = useCallback(async () => {
    if (!activeSlug) return;
    const requestId = refreshRequest.current + 1;
    refreshRequest.current = requestId;
    try {
      const result = await loadWishlist(activeSlug);
      if (requestId !== refreshRequest.current) return;
      setWishlist(result.wishlist);
      lastAvailableSlug.current = activeSlug;
      setLoadedSlug(activeSlug);
      setCollectionState('ready');
      setWishes(
        result.wishlist.wishes.map((wish, index) => ({
          id: wish.id,
          number: String(index + 1).padStart(2, '0'),
          title: wish.title,
          note: wish.note,
          image: wish.imageUrl,
          merchant: wish.merchant,
          price: wish.priceCents / 100,
          pledged: wish.pledgedCents / 100,
          pledgers: wish.contributorCount,
          mode: wish.mode,
          shape:
            index % 3 === 0
              ? 'wish--wide'
              : index % 3 === 1
              ? 'wish--portrait'
              : 'wish--tall',
          sourceUrl: wish.sourceUrl,
          edition: wish.edition || undefined,
          status:
            wish.status === 'available'
              ? undefined
              : wishStatusLabel(wish.status),
          myContributionId: wish.myContributionId,
          myReservationId: wish.myReservationId,
          isReserved: wish.isReserved,
          hasCaptain: wish.hasCaptain,
          deadline: wish.deadline || undefined,
          messageRevealed: wish.messageRevealed,
          giftMessages: wish.giftMessages,
          giftMessageCount: wish.giftMessageCount,
        }))
      );
      setCaptains(
        result.wishlist.wishes
          .filter(wish => wish.amCaptain)
          .map(wish => wish.id)
      );
      setPledges([]);
      setCloudMode(true);
      setServiceUnavailable(false);
    } catch (error) {
      if (requestId !== refreshRequest.current) return;
      const missing = error instanceof SouhaitApiError && error.status === 404;
      setLoadedSlug(activeSlug);
      setCollectionState(missing ? 'missing' : 'unavailable');
      if (missing || lastAvailableSlug.current !== activeSlug) {
        setWishlist(null);
        setWishes(activeSlug === 'julien' && !missing ? initialWishes : []);
        setCaptains([]);
        setPledges([]);
      }
      setCloudMode(false);
      setServiceUnavailable(!DEMO_MODE);
    }
  }, [activeSlug]);

  const refreshOwnerLists = useCallback(async () => {
    try {
      const result = await listOwnerWishlists();
      setOwnerAccount(result.account);
      setOwnerLists(result.wishlists);
      setOwnerSessionError(false);
    } catch (error) {
      setOwnerSessionError(true);
      throw error;
    }
  }, []);

  useEffect(() => {
    const onPopState = () => setRoute(window.location.pathname);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    if (routeInfo.kind === 'marketing') document.title = 'Faites un souha.it !';
  }, [routeInfo.kind]);

  useEffect(() => {
    refreshWishlist();
  }, [refreshWishlist]);

  useEffect(() => {
    if (!cloudMode || !activeSlug) return undefined;
    return connectWishlistEvents(activeSlug, refreshWishlist);
  }, [activeSlug, cloudMode, refreshWishlist]);

  useEffect(() => {
    if (!['owner', 'pricing'].includes(routeInfo.kind)) return;
    let cancelled = false;
    setOwnerSessionLoaded(false);
    setOwnerSessionError(false);
    getOwnerSession()
      .then(async result => {
        if (cancelled) return;
        setOwnerAccount(result.account);
        if (result.account) {
          const ownerResult = await listOwnerWishlists();
          if (!cancelled) {
            setOwnerAccount(ownerResult.account);
            setOwnerLists(ownerResult.wishlists);
          }
        } else {
          setOwnerLists([]);
        }
      })
      .catch(error => {
        if (!cancelled) {
          if (error instanceof SouhaitApiError && error.status === 401) {
            setOwnerAccount(null);
            setOwnerLists([]);
          } else {
            setOwnerSessionError(true);
          }
        }
      })
      .finally(() => {
        if (!cancelled) setOwnerSessionLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [routeInfo.kind, ownerSessionAttempt]);

  useEffect(() => {
    if (!ownerAccount || !['owner', 'pricing'].includes(routeInfo.kind)) return;
    const refresh = () => { refreshOwnerLists().catch(() => undefined); };
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [ownerAccount, routeInfo.kind, refreshOwnerLists]);

  useEffect(() => {
    if (
      routeInfo.kind !== 'owner' ||
      routeInfo.slug ||
      !ownerLists.length
    ) {
      return;
    }
    const path = `/me/${encodeURIComponent(ownerLists[0].slug)}${window.location.search}`;
    window.history.replaceState({}, '', path);
    setRoute(window.location.pathname);
  }, [ownerLists, routeInfo.kind, routeInfo.slug]);

  useEffect(() => {
    if (routeInfo.kind === 'owner' && !activeSlug) {
      setWishlist(null);
      setWishes([]);
      setCaptains([]);
      setPledges([]);
    }
  }, [activeSlug, routeInfo.kind]);

  useEffect(() => {
    trackEvent('page_viewed', routeInfo.kind, {}).catch(() => undefined);
  }, [routeInfo.kind]);

  useEffect(() => {
    if (!DEMO_MODE) return;
    window.localStorage.setItem(WISHES_KEY, JSON.stringify(wishes));
  }, [wishes]);

  useEffect(() => {
    if (!DEMO_MODE) return;
    window.localStorage.setItem(PLEDGES_KEY, JSON.stringify(pledges));
  }, [pledges]);

  useEffect(() => {
    if (!DEMO_MODE) return;
    window.localStorage.setItem(CAPTAINS_KEY, JSON.stringify(captains));
  }, [captains]);

  const navigate = (path: string) => {
    window.history.pushState({}, '', path);
    setRoute(window.location.pathname);
    window.scrollTo({top: 0, behavior: 'auto'});
  };

  if (routeInfo.kind === 'public' && activeSlug) {
    const state = loadedSlug !== activeSlug || collectionState === 'loading' ? 'loading'
      : collectionState === 'missing' ? 'missing'
      : collectionState === 'unavailable' && !wishes.length && !DEMO_MODE ? 'unavailable'
      : routeInfo.wishId && !wishes.some(wish => wish.id === routeInfo.wishId) ? 'missing-wish'
      : null;
    if (state) return <CollectionStatusPage
      navigate={navigate}
      state={state}
      listPath={`/${encodeURIComponent(activeSlug)}`}
      onRetry={() => { setCollectionState('loading'); refreshWishlist(); }}
    />;
    return (
      <PublicListPage
        key={activeSlug}
        navigate={navigate}
        wishlist={wishlist}
        slug={activeSlug}
        wishes={wishes}
        pledges={pledges}
        captains={captains}
        setPledges={setPledges}
        setCaptains={setCaptains}
        onRefresh={refreshWishlist}
        cloudMode={cloudMode}
        serviceUnavailable={serviceUnavailable}
        selectedWishId={routeInfo.wishId}
      />
    );
  }

  if (routeInfo.kind === 'pricing') {
    return (
      <PricingPage
        navigate={navigate}
        account={ownerAccount}
        wishlists={ownerLists}
        onCheckout={async (kind, wishlistId = '') => {
          const result = await startBillingCheckout(kind, wishlistId);
          redirectToBilling(result.url);
        }}
        onPortal={async () => {
          const result = await openBillingPortal();
          redirectToBilling(result.url);
        }}
      />
    );
  }

  if (routeInfo.kind === 'privacy' || routeInfo.kind === 'terms') {
    return (
      <LegalPage
        navigate={navigate}
        kind={routeInfo.kind === 'privacy' ? 'privacy' : 'terms'}
      />
    );
  }

  if (routeInfo.kind === 'owner') {
    if (!ownerSessionLoaded) {
      return (
        <main className="souhait owner-loading" role="status">
          <span className="sui-spinner" aria-hidden="true" />
          <p>Ouverture de votre espace…</p>
        </main>
      );
    }
    if (ownerSessionError) {
      return <CollectionStatusPage navigate={navigate} state="owner-unavailable" listPath="/me" onRetry={() => setOwnerSessionAttempt(current => current + 1)} />;
    }
    if (!ownerAccount) {
      return (
        <OwnerLoginPage
          navigate={navigate}
          onAuthenticated={async account => {
            setOwnerAccount(account);
            setOwnerSessionLoaded(true);
            await refreshOwnerLists();
            const offer = new URLSearchParams(window.location.search).get('offer');
            if (offer === 'monthly' || offer === 'annual') navigate(`/pricing?offer=${offer}`);
          }}
        />
      );
    }
    const ownerSlug = activeSlug || (ownerLists[0] && ownerLists[0].slug) || '';
    return (
      <OwnerPage
        navigate={navigate}
        account={ownerAccount}
        ownerLists={ownerLists}
        currentSlug={ownerSlug}
        wishes={wishes}
        onAddWish={async wish => {
          if (!cloudMode || !activeSlug) {
            if (!DEMO_MODE) {
              throw new Error('La liste est momentanément indisponible. Réessayez dans quelques instants.');
            }
            setWishes(current => [...current, wish]);
            return;
          }
          await createOwnerWish(activeSlug, {
            title: wish.title,
            note: wish.note,
            edition: wish.edition,
            imageUrl: directionSixImage(wish.image),
            merchant: wish.merchant,
            sourceUrl: wish.sourceUrl,
            priceCents: Math.round(wish.price * 100),
            mode: wish.mode,
            deadline: wish.deadline || '',
          });
          await refreshWishlist();
          await refreshOwnerLists();
        }}
        onUpdateWish={async wish => {
          if (!cloudMode) {
            if (!DEMO_MODE) {
              throw new Error('The owner API is temporarily unavailable.');
            }
            setWishes(current =>
              current.map(item => (item.id === wish.id ? wish : item))
            );
            return;
          }
          await updateOwnerWish(wish.id, {
            title: wish.title,
            note: wish.note,
            edition: wish.edition,
            imageUrl: directionSixImage(wish.image),
            merchant: wish.merchant,
            sourceUrl: wish.sourceUrl,
            priceCents: Math.round(wish.price * 100),
            mode: wish.mode,
            deadline: wish.deadline || '',
          });
          await refreshWishlist();
        }}
        onArchiveWish={async wish => {
          if (!cloudMode) {
            if (!DEMO_MODE) {
              throw new Error('The owner API is temporarily unavailable.');
            }
            setWishes(current => current.filter(item => item.id !== wish.id));
            return;
          }
          await archiveOwnerWish(wish.id);
          await refreshWishlist();
          await refreshOwnerLists();
        }}
        onCreateList={async input => {
          const result = await createOwnerWishlist(input);
          setOwnerLists(current => [...current, result.wishlist]);
          navigate(`/me/${encodeURIComponent(result.wishlist.slug)}`);
        }}
        onLogout={async () => {
          await logoutOwner();
          setOwnerAccount(null);
          setOwnerLists([]);
          navigate('/');
        }}
        onBillingPortal={async () => {
          const result = await openBillingPortal();
          redirectToBilling(result.url);
        }}
        onUpdatePayment={async (slug, paymentUrl, paymentLabel) => {
          await updateOwnerWishlistPayment(slug, paymentUrl, paymentLabel);
          await refreshOwnerLists();
          await refreshWishlist();
        }}
        onRefreshAccount={refreshOwnerLists}
      />
    );
  }

  if (routeInfo.kind === 'activity') {
    return <ContributorActivityPage navigate={navigate} />;
  }

  return <MarketingPage navigate={navigate} />;
};

export default Souhait;
